import { useEffect, useRef, useState, useCallback, useMemo, memo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api, { buildWebSocketUrl, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import {
  Send, Mic, Square, Search, Smile, Paperclip, MoreVertical,
  ArrowLeft, Bot, Check, CheckCheck, Volume2, X, ChevronUp, ChevronDown,
  Receipt, Clock, TrendingUp, CheckCircle2, PanelRightClose, PanelRightOpen,
  User, MessagesSquare, FileText, Maximize2, Minimize2,
  Play, Pause, PauseCircle, Image as ImageIcon, Video, Smartphone,
} from "lucide-react";
import { formatCurrency, formatDateTime, formatRelative } from "@/lib/format";

/** Cor de marca (chat / leads) — alinhada à paleta ConvertPix */
const BRAND_PRIMARY = "#22C55E";
const BRAND_PRIMARY_DEEP = "#16A34A";

/** ~9 MB base64 — alinhado ao limite do backend / MongoDB. */
const MAX_CHAT_FILE_BYTES = 9 * 1024 * 1024;

function toastApiError(e, fallback) {
  const d = e?.response?.data?.detail;
  if (typeof d === "string" && d.trim()) {
    toast.error(d.length > 280 ? `${d.slice(0, 280)}…` : d);
    return;
  }
  if (Array.isArray(d) && d.length) {
    const s = d.map((x) => (typeof x === "object" && x?.msg ? x.msg : String(x))).join(" ");
    toast.error(s || fallback);
    return;
  }
  if (d != null && typeof d === "object") {
    const s = formatApiError(d);
    if (s && s !== "Algo deu errado. Tente novamente.") {
      toast.error(s.length > 320 ? `${s.slice(0, 320)}…` : s);
      return;
    }
  }
  toast.error(fallback);
}

/** Extensões comuns quando `File.type` vem vazio (ex. Windows / alguns .jpeg). */
const CHAT_EXT_TO_MIME = {
  jpg: "image/jpeg", jpeg: "image/jpeg", jfif: "image/jpeg", jpe: "image/jpeg", pjpeg: "image/jpeg",
  png: "image/png", apng: "image/apng", gif: "image/gif", webp: "image/webp", bmp: "image/bmp",
  tif: "image/tiff", tiff: "image/tiff", heic: "image/heic", heif: "image/heif", avif: "image/avif",
  svg: "image/svg+xml", ico: "image/x-icon",
  mp4: "video/mp4", m4v: "video/x-m4v", mov: "video/quicktime", qt: "video/quicktime",
  webm: "video/webm", mkv: "video/x-matroska", avi: "video/x-msvideo", "3gp": "video/3gpp",
  mpeg: "video/mpeg", mpg: "video/mpeg", ogv: "video/ogg", wmv: "video/x-ms-wmv", flv: "video/x-flv",
};

function guessMimeFromFilename(filename) {
  const ext = (filename || "").split(".").pop()?.toLowerCase();
  if (!ext) return null;
  return CHAT_EXT_TO_MIME[ext] || null;
}

/** menuIntent: qual opção do menu do clipe abriu o seletor — evita classificar .jpeg como documento. */
function inferChatMedia(file, menuIntent) {
  const rawName = file.name || "";
  const name = rawName.trim() ? rawName : (menuIntent === "image" ? "foto.jpg" : menuIntent === "video" ? "video.mp4" : "anexo");
  const t = (file.type || "").toLowerCase();
  const guessed = guessMimeFromFilename(name);

  if (t.startsWith("image/")) {
    return { kind: "image", mimetype: file.type || "image/jpeg", name };
  }
  if (t.startsWith("video/")) {
    return { kind: "video", mimetype: file.type || "video/mp4", name };
  }
  if (menuIntent === "image") {
    return {
      kind: "image",
      mimetype: guessed?.startsWith("image/") ? guessed : "image/jpeg",
      name,
    };
  }
  if (menuIntent === "video") {
    return {
      kind: "video",
      mimetype: guessed?.startsWith("video/") ? guessed : "video/mp4",
      name,
    };
  }
  if (guessed?.startsWith("image/")) return { kind: "image", mimetype: guessed, name };
  if (guessed?.startsWith("video/")) return { kind: "video", mimetype: guessed, name };
  return {
    kind: "document",
    mimetype: (file.type && file.type.trim()) ? file.type : "application/octet-stream",
    name,
  };
}
const LIGHT_BG = "#f0f2f6";

const CHAT_PATTERN = `data:image/svg+xml;utf8,${encodeURIComponent(
  `<svg xmlns='http://www.w3.org/2000/svg' width='80' height='80' viewBox='0 0 80 80'><g fill='%23cbd5e1' fill-opacity='0.45'><circle cx='8' cy='8' r='1.2'/><circle cx='40' cy='24' r='1'/><circle cx='64' cy='56' r='1.1'/></g></svg>`
)}`;

function formatFileSize(bytes) {
  if (bytes == null || Number.isNaN(bytes)) return "";
  const u = ["B", "KB", "MB", "GB"];
  let n = bytes;
  let i = 0;
  while (n >= 1024 && i < u.length - 1) {
    n /= 1024;
    i += 1;
  }
  return `${n < 10 && i > 0 ? n.toFixed(1) : Math.round(n)} ${u[i]}`;
}

function formatTime(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" }); } catch { return ""; }
}

function formatContactTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  if (d.toDateString() === now.toDateString()) return formatTime(iso);
  const y = new Date(now); y.setDate(now.getDate() - 1);
  if (d.toDateString() === y.toDateString()) return "ontem";
  return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });
}

function isContactOnlineNow(contact) {
  const ts = Date.parse(contact?.last_message_at || "");
  if (!Number.isFinite(ts)) return false;
  // Heurística simples de presença: atividade nos últimos 3 minutos.
  return Date.now() - ts <= 3 * 60 * 1000;
}

/** Cor estável por nome da instância (badges no chat e na lista). */
function instanceBadgeStyle(label) {
  const s = String(label || "WA");
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h + s.charCodeAt(i) * 17) % 360;
  return { backgroundColor: `hsl(${h} 62% 38%)`, color: "#fff" };
}

/** duração em mm:ss para pré-visualização na lista (WhatsApp-style). */
function formatVoiceDurationSeconds(sec) {
  if (sec == null || Number.isNaN(Number(sec)) || Number(sec) < 0) return null;
  const s = Math.floor(Number(sec));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${String(r).padStart(2, "0")}`;
}

function lastPreviewFromMessage(msg) {
  if (!msg) return "";
  if (msg.type === "audio") {
    const fd = formatVoiceDurationSeconds(msg.audio_duration_sec);
    return fd ? `Mensagem de voz (${fd})` : "Mensagem de voz";
  }
  if (msg.type === "image") return (msg.text || "").trim() || "Foto";
  if (msg.type === "video") return (msg.text || "").trim() || "Vídeo";
  if (msg.type === "document") return (msg.text || "").trim() || "Documento";
  return msg.text || "";
}

function normalizePhoneLoose(phone) {
  return String(phone || "").replace(/\D+/g, "");
}

function samePhoneContact(a, b) {
  const ap = normalizePhoneLoose(a?.phone);
  const bp = normalizePhoneLoose(b?.phone);
  if (!ap || !bp) return false;
  return ap === bp || ap.endsWith(bp) || bp.endsWith(ap);
}

function newTraceId(prefix = "cpx") {
  return `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

function waveformBarsFromSeed(seed, n) {
  const s = String(seed || "x");
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  const out = [];
  for (let i = 0; i < n; i++) {
    h = (h * 1103515245 + 12345) >>> 0;
    out.push(18 + (h % 55));
  }
  return out;
}

/** Player de nota de voz estilo WhatsApp (barra + avatar + ondas). */
function VoiceNotePlayer({
  src,
  isOut,
  peerAvatar,
  peerName,
  operatorInitials,
  operatorWaAvatar,
  durationHintSec,
  msgId,
  loading: mediaLoading = false,
}) {
  const SPEED_OPTIONS = [1, 1.5, 2];
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [speed, setSpeed] = useState(1);
  const [duration, setDuration] = useState(
    durationHintSec != null && durationHintSec >= 0 ? Number(durationHintSec) : 0,
  );
  const bars = useMemo(() => waveformBarsFromSeed(msgId, 28), [msgId]);
  const thumbColor = isOut ? "#3b82f6" : "#22c55e";
  const trackBg = isOut ? "rgba(59,130,246,0.12)" : "rgba(34,197,94,0.12)";

  useEffect(() => {
    const a = audioRef.current;
    if (!a || !src) return undefined;
    const onMeta = () => {
      const d = a.duration;
      if (d && Number.isFinite(d) && d > 0) setDuration(Math.floor(d));
    };
    const onTime = () => {
      if (a.duration && Number.isFinite(a.duration)) setProgress(a.currentTime / a.duration);
      else setProgress(0);
    };
    const onEnded = () => {
      setPlaying(false);
      setProgress(0);
      a.currentTime = 0;
    };
    a.addEventListener("loadedmetadata", onMeta);
    a.addEventListener("timeupdate", onTime);
    a.addEventListener("ended", onEnded);
    return () => {
      a.removeEventListener("loadedmetadata", onMeta);
      a.removeEventListener("timeupdate", onTime);
      a.removeEventListener("ended", onEnded);
    };
  }, [src]);

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    a.playbackRate = speed;
  }, [speed]);

  const toggle = () => {
    const a = audioRef.current;
    if (!a || !src) return;
    if (playing) {
      a.pause();
      setPlaying(false);
    } else {
      void a.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
    }
  };

  const onSeek = (e) => {
    const a = audioRef.current;
    if (!a || !a.duration) return;
    const v = Number(e.target.value);
    a.currentTime = v * a.duration;
    setProgress(v);
  };

  const changeSpeed = () => {
    const idx = SPEED_OPTIONS.indexOf(speed);
    const next = SPEED_OPTIONS[(idx + 1) % SPEED_OPTIONS.length];
    setSpeed(next);
  };

  const curSec = duration * progress;
  const dispCur = formatVoiceDurationSeconds(Number.isFinite(curSec) ? curSec : 0) || "0:00";
  const durForLabel = duration > 0 ? duration : (durationHintSec != null && durationHintSec >= 0 ? Number(durationHintSec) : 0);
  const dispDur = formatVoiceDurationSeconds(durForLabel) || "0:00";

  const playBtn = (
    <button
      type="button"
      onClick={toggle}
      className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full transition ${
        isOut
          ? "bg-white/95 text-slate-700 border border-slate-200/80 shadow-sm hover:bg-white"
          : "bg-slate-100 text-slate-700 hover:bg-slate-200"
      }`}
      aria-label={playing ? "Pausar" : "Reproduzir"}
    >
      {playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4 pl-0.5" />}
    </button>
  );

  const avatarInner = (
    <div className="relative h-9 w-9 shrink-0 rounded-full overflow-hidden border border-slate-200/90 bg-slate-200 shadow-sm">
      {isOut ? (
        operatorWaAvatar ? (
          <img src={operatorWaAvatar} alt="" className="h-full w-full object-cover" />
        ) : (
          <div className="h-full w-full flex items-center justify-center text-[11px] font-bold text-slate-600 bg-slate-100">
            {operatorInitials}
          </div>
        )
      ) : peerAvatar ? (
        <img src={peerAvatar} alt={peerName || ""} className="h-full w-full object-cover" />
      ) : (
        <div className="h-full w-full flex items-center justify-center text-[11px] font-bold text-white" style={{ background: `linear-gradient(145deg, ${BRAND_PRIMARY}, ${BRAND_PRIMARY_DEEP})` }}>
          {(peerName || "?").slice(0, 2).toUpperCase()}
        </div>
      )}
      <span
        className={`absolute -bottom-0.5 flex h-4 w-4 items-center justify-center rounded-full border border-white shadow ${
          isOut ? "-right-0.5" : "-left-0.5"
        }`}
        style={{ backgroundColor: BRAND_PRIMARY }}
      >
        <Mic className="h-2 w-2 text-white" strokeWidth={2.5} />
      </span>
    </div>
  );

  if (mediaLoading && !src) {
    return (
      <div className="flex items-center gap-2 py-2 min-w-[220px] max-w-[300px] text-slate-500 text-[13px]">
        <span className="h-1.5 w-1.5 shrink-0 animate-pulse rounded-full bg-slate-400" aria-hidden />
        <span>Carregando áudio…</span>
      </div>
    );
  }

  if (!src) {
    return (
      <div className="flex items-center gap-2 py-1 min-w-[220px] max-w-[300px] text-slate-500 text-[13px]">
        <Volume2 className="h-4 w-4 shrink-0 opacity-60" />
        <span>Áudio indisponível (não foi possível carregar a mídia).</span>
      </div>
    );
  }

  return (
    <div className="flex min-w-[240px] max-w-[min(320px,88vw)] flex-col gap-2 py-1">
      <audio ref={audioRef} src={src} preload="metadata" className="hidden" />
      <div className="flex items-end gap-2">
        {isOut ? (
          <>
            {avatarInner}
            {playBtn}
          </>
        ) : (
          playBtn
        )}
        <div className="relative flex min-h-[38px] min-w-0 flex-1 items-end gap-px px-0.5" style={{ opacity: 0.88 }}>
          {bars.map((h, i) => {
            const filled = (i + 1) / bars.length <= progress;
            const px = Math.max(8, Math.min(34, Math.round(h * 0.42)));
            return (
              <span
                key={i}
                className="w-[3px] shrink-0 rounded-full transition-colors duration-150"
                style={{
                  height: px,
                  alignSelf: "flex-end",
                  backgroundColor: filled ? thumbColor : "#cbd5e1",
                }}
              />
            );
          })}
        </div>
        {!isOut ? avatarInner : null}
      </div>
      <input
        type="range"
        min={0}
        max={1}
        step={0.001}
        value={progress}
        onChange={onSeek}
        className={`h-1.5 w-full cursor-pointer rounded-full ${isOut ? "accent-blue-500" : "accent-[#22C55E]"}`}
        style={{
          background: `linear-gradient(to right, ${thumbColor} 0%, ${thumbColor} ${progress * 100}%, ${trackBg} ${progress * 100}%, ${trackBg} 100%)`,
        }}
        aria-label="Posição do áudio"
      />
      <div className="flex items-center justify-between gap-2 px-0.5 text-[11px] tabular-nums text-slate-500">
        <span>{dispCur}</span>
        <button
          type="button"
          onClick={changeSpeed}
          className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold transition ${
            isOut
              ? "border-blue-200 bg-blue-50/80 text-blue-700 hover:bg-blue-100"
              : "border-emerald-200 bg-emerald-50/80 text-emerald-700 hover:bg-emerald-100"
          }`}
          title="Alterar velocidade do áudio"
          aria-label="Alterar velocidade do áudio"
        >
          {speed}x
        </button>
        <span>{dispDur}</span>
      </div>
    </div>
  );
}

/**
 * WebSocket estável: não recria conexão quando callbacks mudam.
 * Reconexão com backoff + resync opcional ao reconectar.
 */
function useChatWebSocket({ onEvent, onOpen, onConnectionChange }) {
  const onEventRef = useRef(onEvent);
  const onOpenRef = useRef(onOpen);
  const onConnRef = useRef(onConnectionChange);
  useEffect(() => { onEventRef.current = onEvent; }, [onEvent]);
  useEffect(() => { onOpenRef.current = onOpen; }, [onOpen]);
  useEffect(() => { onConnRef.current = onConnectionChange; }, [onConnectionChange]);

  useEffect(() => {
    let stopped = false;
    let attempt = 0;
    let ws = null;
    let reconnectTimer = null;
    let pingTimer = null;

    const clearPing = () => {
      if (pingTimer) {
        clearInterval(pingTimer);
        pingTimer = null;
      }
    };

    const connect = () => {
      if (stopped) return;
      clearPing();
      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }
      try {
        ws?.close();
      } catch {
        /* ignore */
      }

      const wsUrl = buildWebSocketUrl();
      try {
        ws = new WebSocket(wsUrl);
      } catch {
        onConnRef.current?.(false);
        if (!stopped) {
          const delay = Math.min(30000, 1000 * Math.pow(2, Math.min(attempt, 5)));
          attempt += 1;
          reconnectTimer = setTimeout(connect, delay);
        }
        return;
      }

      ws.onopen = () => {
        attempt = 0;
        onConnRef.current?.(true);
        try {
          onOpenRef.current?.();
        } catch {
          /* ignore */
        }
        pingTimer = setInterval(() => {
          if (ws && ws.readyState === WebSocket.OPEN) ws.send("ping");
        }, 25000);
      };

      ws.onmessage = (e) => {
        try {
          const payload = JSON.parse(e.data);
          if (payload.event === "pong") return;
          onEventRef.current?.(payload);
        } catch {
          /* ignore */
        }
      };

      ws.onerror = () => {
        try {
          ws?.close();
        } catch {
          /* ignore */
        }
      };

      ws.onclose = () => {
        clearPing();
        onConnRef.current?.(false);
        if (stopped) return;
        const delay = Math.min(30000, 1000 * Math.pow(2, Math.min(attempt, 5)));
        attempt += 1;
        reconnectTimer = setTimeout(connect, delay);
      };
    };

    connect();

    return () => {
      stopped = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      clearPing();
      try {
        ws?.close();
      } catch {
        /* ignore */
      }
    };
  }, []);
}

// Highlight search matches in text
function HighlightedText({ text, query }) {
  if (!query || !text) return <>{text}</>;
  const parts = text.split(new RegExp(`(${query.replace(/[-/\\^$*+?.()|[\]{}]/g, "\\$&")})`, "gi"));
  return (
    <>
      {parts.map((p, i) =>
        p.toLowerCase() === query.toLowerCase() ? <mark key={i} className="chat-highlight">{p}</mark> : p
      )}
    </>
  );
}

function ContactRowInner({ contact, selected, onClick }) {
  const initials = (contact.name || "?").slice(0, 2).toUpperCase();
  const isOnline = isContactOnlineNow(contact);
  return (
    <button
      onClick={onClick}
      data-testid={`contact-row-${contact.id}`}
      className={`w-full text-left px-4 py-3.5 transition-all flex items-center gap-3 group relative rounded-2xl mx-2 mb-1 ${
        selected ? "bg-white shadow-sm ring-1 ring-slate-200/80" : "hover:bg-white/70"
      }`}
    >
      {selected && (
        <div
          className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-10 rounded-r-full"
          style={{ backgroundColor: BRAND_PRIMARY }}
        />
      )}
      <div
        className={`relative h-12 w-12 rounded-full flex items-center justify-center font-semibold text-sm shrink-0 transition shadow-inner overflow-hidden ${
          contact.is_lead ? "text-white" : "bg-slate-200 text-slate-600"
        }`}
        style={contact.is_lead ? { background: `linear-gradient(145deg, ${BRAND_PRIMARY}, ${BRAND_PRIMARY_DEEP})` } : undefined}
      >
        {contact.profile_picture_url ? (
          <img src={contact.profile_picture_url} alt={contact.name || "Contato"} className="h-full w-full object-cover" />
        ) : (
          initials
        )}
        {isOnline && (
          <span
            className="absolute -top-0.5 -right-0.5 h-3 w-3 rounded-full ring-2 ring-white"
            style={{ backgroundColor: BRAND_PRIMARY }}
            title="Online agora"
          />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <div className="font-semibold text-[15px] text-slate-800 truncate flex items-center gap-1.5">
            {contact.name}
            {contact.is_lead && (
              <span
                title="Lead com PIX"
                className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-md font-bold border"
                style={{ color: BRAND_PRIMARY, borderColor: `${BRAND_PRIMARY}55`, background: `${BRAND_PRIMARY}18` }}
              >
                LEAD
              </span>
            )}
            {contact.last_message_instance_name && (
              <span
                title={`Última mensagem via ${contact.last_message_instance_name}`}
                className="text-[9px] font-bold px-1.5 py-0.5 rounded-md truncate max-w-[100px] shrink-0"
                style={instanceBadgeStyle(contact.last_message_instance_name)}
              >
                {contact.last_message_instance_name}
              </span>
            )}
            {contact.mute_automation && (
              <PauseCircle className="h-3.5 w-3.5 text-amber-500 shrink-0" aria-label="Automação pausada" />
            )}
          </div>
          <div
            className={`text-[11px] shrink-0 ${contact.unread_count > 0 ? "font-bold" : "text-slate-400"}`}
            style={contact.unread_count > 0 ? { color: BRAND_PRIMARY } : undefined}
          >
            {formatContactTime(contact.last_message_at)}
          </div>
        </div>
        <div className="flex items-center justify-between gap-2 mt-0.5">
          <div className="text-[13px] text-slate-500 truncate">
            {(() => {
              const p = contact.last_message_preview || "";
              if (p === "[audio]" || p.toLowerCase() === "[audio]") return "Mensagem de voz";
              return p || contact.phone;
            })()}
          </div>
          {contact.unread_count > 0 && (
            <span
              className="min-w-[22px] h-[22px] px-1.5 rounded-full text-white text-[11px] font-bold flex items-center justify-center shadow-md"
              style={{ backgroundColor: BRAND_PRIMARY }}
            >
              {contact.unread_count}
            </span>
          )}
        </div>
      </div>
    </button>
  );
}

const ContactRow = memo(ContactRowInner, (p, n) => {
  if (p.selected !== n.selected) return false;
  const a = p.contact;
  const b = n.contact;
  return (
    a.id === b.id &&
    a.unread_count === b.unread_count &&
    (a.last_message_at || "") === (b.last_message_at || "") &&
    (a.last_message_preview || "") === (b.last_message_preview || "") &&
    (a.name || "") === (b.name || "") &&
    (a.profile_picture_url || "") === (b.profile_picture_url || "") &&
    Boolean(a.is_lead) === Boolean(b.is_lead) &&
    Boolean(a.mute_automation) === Boolean(b.mute_automation) &&
    (a.last_message_instance_name || "") === (b.last_message_instance_name || "") &&
    (a.conversation_instance_name || "") === (b.conversation_instance_name || "") &&
    (a.conversation_instance_id || "") === (b.conversation_instance_id || "")
  );
});

function MessageBubble({
  msg,
  contactId,
  showTail,
  query,
  matched,
  isCurrentMatch,
  innerRef,
  leadAvatar,
  leadName,
  operatorInitials,
  operatorWaAvatar,
  onOpenMedia,
}) {
  const inlineB64 = String(msg.media_base64 || "").trim();
  const lazyType = ["image", "audio", "video", "document"].includes(msg.type);
  const shouldLazyFetch = Boolean(contactId && msg?.id && lazyType && !inlineB64);
  const [resolvedB64, setResolvedB64] = useState(() => inlineB64 || null);
  const [mediaLoading, setMediaLoading] = useState(() => shouldLazyFetch);
  const [mediaErr, setMediaErr] = useState(false);

  useEffect(() => {
    const inline = String(msg.media_base64 || "").trim();
    if (inline) {
      setResolvedB64(inline);
      setMediaErr(false);
      setMediaLoading(false);
      return undefined;
    }
    if (!contactId || !msg?.id) {
      setResolvedB64(null);
      setMediaErr(false);
      setMediaLoading(false);
      return undefined;
    }
    const t = msg.type;
    if (!["image", "audio", "video", "document"].includes(t)) {
      setResolvedB64(null);
      setMediaErr(false);
      setMediaLoading(false);
      return undefined;
    }
    let cancelled = false;
    setMediaLoading(true);
    setMediaErr(false);
    api
      .get(`/chat/contacts/${contactId}/messages/${msg.id}/media`)
      .then(({ data }) => {
        const b = data?.media_base64 != null ? String(data.media_base64).trim() : "";
        if (!cancelled) {
          if (b) setResolvedB64(b);
          else setMediaErr(true);
        }
      })
      .catch(() => {
        if (!cancelled) setMediaErr(true);
      })
      .finally(() => {
        if (!cancelled) setMediaLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [contactId, msg.id, msg.type, msg.media_base64]);

  if (msg._local_ui === "sending") {
    return (
      <div
        ref={innerRef}
        className="flex justify-center px-3 py-2 msg-enter"
        data-testid={`msg-${msg.id}`}
      >
        <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-200/90 bg-slate-100/90 px-3 py-1.5 text-[11px] font-medium text-slate-600 shadow-sm">
          <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-slate-400" aria-hidden />
          {msg.text || "Enviando…"}
        </span>
        <span className="sr-only">Apenas visível para você, não enviado ao contato.</span>
      </div>
    );
  }

  const isOut = msg.direction === "out";
  const sendFailed = isOut && msg.delivery_status === "failed";
  const ds = String(msg.delivery_status || "").toLowerCase();
  const sendPending =
    isOut &&
    ["pending", "queued", "sending", ""].includes(ds);
  const sendDelivered = isOut && ["delivered", "read"].includes(ds);
  const sendSent = isOut && !sendFailed && !sendPending && !sendDelivered;
  const outBg = sendFailed ? "#FEF2F2" : "#E8EAF6";
  const outTail = sendFailed ? "#FEF2F2" : "#E8EAF6";
  const ringClass = isCurrentMatch
    ? "ring-2 ring-amber-400 ring-offset-2 ring-offset-[#f8fafc]"
    : matched
      ? "ring-1 ring-amber-300/80"
      : "";

  const statusIcon = !isOut ? null : sendFailed ? (
    <X className="h-3.5 w-3.5 text-red-600" strokeWidth={2.5} aria-hidden />
  ) : sendPending ? (
    <Clock className="h-3.5 w-3.5 text-slate-400" strokeWidth={2.5} aria-hidden />
  ) : sendSent ? (
    <Check className="h-3.5 w-3.5 text-slate-500" strokeWidth={2.5} aria-hidden />
  ) : (
    <CheckCheck
      className="h-3.5 w-3.5"
      style={{
        color: ds === "read"
          ? BRAND_PRIMARY
          : ds === "delivered"
            ? "#64748b"
            : "#94a3b8",
      }}
      strokeWidth={2.5}
    />
  );

  return (
    <div ref={innerRef} className={`flex min-w-0 ${isOut ? "justify-end" : "justify-start"} px-3 msg-enter`} data-testid={`msg-${msg.id}`}>
      <div
        className={`relative min-w-0 w-fit max-w-[85%] md:max-w-[68%] lg:max-w-[62%] rounded-2xl px-3 py-2 shadow-sm border transition-all ${ringClass} ${
          isOut ? (sendFailed ? "border-red-300/90" : "border-slate-200/60") : "bg-white border-slate-200/80"
        } ${msg.type === "audio" || msg.type === "video" ? "pb-8" : ""}`}
        style={isOut ? { backgroundColor: outBg } : undefined}
      >
        {showTail && (
          <span
            className={`absolute top-3 ${isOut ? "-right-[6px]" : "-left-[6px]"}`}
            style={{
              width: 0,
              height: 0,
              borderStyle: "solid",
              borderWidth: isOut ? "0 0 9px 7px" : "0 7px 9px 0",
              borderColor: isOut
                ? `transparent transparent transparent ${outTail}`
                : "transparent #ffffff transparent transparent",
            }}
          />
        )}
        <div className="flex items-start justify-between gap-2 mb-0.5 min-h-[14px]">
          {msg.instance?.display_name ? (
            <span
              className="text-[10px] font-bold px-2 py-0.5 rounded-md truncate max-w-[70%]"
              style={instanceBadgeStyle(msg.instance.display_name)}
              title={msg.instance.display_name}
            >
              {msg.instance.display_name}
            </span>
          ) : (
            <span />
          )}
          <span
            className={`text-[9px] uppercase tracking-wide shrink-0 pt-0.5 ${sendFailed ? "text-red-600 font-semibold" : "text-slate-400"}`}
          >
            {isOut ? (sendFailed ? "Falha" : sendPending ? "Enviando" : sendDelivered ? "Entregue" : "Enviada") : "Recebida"}
          </span>
        </div>
        {sendFailed && (
          <div className="mb-2 rounded-lg border border-red-200 bg-red-50/95 px-2.5 py-2 text-[12px] font-semibold text-red-900">
            Falha no envio da mensagem
            {(msg.correlation_id || msg.trace_id) ? (
              <div className="mt-1 text-[10px] font-mono text-red-800/90">
                Correlation: {msg.correlation_id || msg.trace_id}
              </div>
            ) : null}
            {msg.send_error ? (
              <div className="mt-1 text-[11px] font-normal text-red-800/95 whitespace-pre-wrap break-words">
                {msg.send_error}
              </div>
            ) : null}
          </div>
        )}
        {msg.type === "image" && (resolvedB64 || mediaLoading || mediaErr) && (
          <div className="rounded-xl overflow-hidden -mx-0.5 mb-1 shadow-md border border-slate-200/80">
            {mediaLoading && !resolvedB64 ? (
              <div className="flex min-h-[120px] max-w-[320px] items-center justify-center gap-2 bg-slate-100/90 px-4 py-8 text-[13px] text-slate-500">
                <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-slate-400" aria-hidden />
                Carregando imagem…
              </div>
            ) : mediaErr && !resolvedB64 ? (
              <div className="flex min-h-[80px] max-w-[320px] items-center px-4 py-6 text-[13px] text-slate-500">
                Não foi possível carregar a imagem.
              </div>
            ) : (
              <button
                type="button"
                onClick={() =>
                  onOpenMedia?.({
                    type: "image",
                    src: `data:${msg.mimetype || "image/jpeg"};base64,${resolvedB64}`,
                    name: msg.text || "Imagem",
                  })
                }
                className="block w-full text-left"
                title="Abrir imagem"
              >
                <img src={`data:${msg.mimetype || "image/jpeg"};base64,${resolvedB64}`} alt="" className="w-full max-w-[320px] block" />
              </button>
            )}
          </div>
        )}
        {msg.type === "audio" && (
          <VoiceNotePlayer
            src={resolvedB64 ? `data:${(msg.mimetype || "audio/ogg").split(";")[0]};base64,${resolvedB64}` : ""}
            loading={mediaLoading && !resolvedB64}
            isOut={isOut}
            peerAvatar={leadAvatar}
            peerName={leadName}
            operatorInitials={operatorInitials}
            operatorWaAvatar={operatorWaAvatar}
            durationHintSec={msg.audio_duration_sec}
            msgId={msg.id}
          />
        )}
        {msg.type === "video" && (resolvedB64 || mediaLoading || mediaErr) && (
          <>
            {mediaLoading && !resolvedB64 ? (
              <div className="flex min-h-[100px] max-w-[320px] items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-4 py-6 my-1 text-[13px] text-slate-500">
                <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-slate-400" aria-hidden />
                Carregando vídeo…
              </div>
            ) : mediaErr && !resolvedB64 ? (
              <div className="my-1 max-w-[320px] rounded-xl border border-slate-200 px-4 py-4 text-[13px] text-slate-500">
                Não foi possível carregar o vídeo.
              </div>
            ) : (
              <video
                controls
                className="rounded-xl max-w-[320px] block my-1 border border-slate-200"
                src={`data:${msg.mimetype || "video/mp4"};base64,${resolvedB64}`}
                title={msg.text || "Vídeo"}
              />
            )}
          </>
        )}
        {msg.type === "document" && (resolvedB64 || mediaLoading || mediaErr) && (
          <>
            {mediaLoading && !resolvedB64 ? (
              <div className="flex items-center gap-2 rounded-xl border px-3 py-2.5 my-1 max-w-[280px] text-[13px] text-slate-500">
                <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-slate-400" aria-hidden />
                Carregando documento…
              </div>
            ) : mediaErr && !resolvedB64 ? (
              <div className="my-1 max-w-[280px] rounded-xl border px-3 py-2.5 text-[13px] text-slate-500">
                Não foi possível carregar o documento.
              </div>
            ) : (
              <a
                href={`data:${(msg.mimetype || "application/octet-stream").split(";")[0]};base64,${resolvedB64}`}
                download={(msg.text || "documento").replace(/[/\\?%*:|"<>]/g, "_").slice(0, 120) || "documento"}
                className={`flex items-center gap-2 rounded-xl border px-3 py-2.5 text-[13px] font-medium my-1 max-w-[280px] transition ${
                  isOut ? "border-slate-200/80 bg-white/60 text-slate-800 hover:bg-white" : "border-slate-200 bg-slate-50 text-slate-800 hover:bg-slate-100"
                }`}
              >
                <FileText className="h-5 w-5 shrink-0 text-slate-500" />
                <span className="min-w-0 truncate">{msg.text || "Documento"}</span>
              </a>
            )}
          </>
        )}
        {msg.text && msg.type !== "document" && (
          <div className="min-w-0 max-w-full pr-14 text-[14.5px] leading-[20px] text-slate-800 whitespace-pre-wrap break-all [overflow-wrap:anywhere]">
            <HighlightedText text={msg.text} query={query} />
          </div>
        )}
        <div className={`flex items-center gap-1 text-[10.5px] text-slate-400 ${msg.type === "video" ? "mt-1 justify-end pr-0.5" : "absolute bottom-1.5 right-2.5"}`}>
          <span>{formatTime(msg.created_at)}</span>
          {statusIcon}
        </div>
      </div>
    </div>
  );
}

function DateSeparator({ label }) {
  return (
    <div className="flex justify-center my-4">
      <span className="text-[11px] font-semibold tracking-wider uppercase text-slate-500 bg-white/90 px-3 py-1.5 rounded-full border border-slate-200 shadow-sm">
        {label}
      </span>
    </div>
  );
}

function ContactProfilePanel({ contact, context, onClose, onMarkPaid, onToggleMute, onSyncProfile, onCopyPhone }) {
  if (!context) {
    return (
      <div className="p-6 text-center text-slate-500 text-sm">Carregando perfil...</div>
    );
  }
  const active = context.active_transaction;
  const s = context.summary;
  return (
    <div className="h-full flex flex-col panel-enter bg-[#f8f9fb]" data-testid="lead-context-panel">
      <header className="h-[56px] px-4 flex items-center justify-between border-b border-slate-200/80 shrink-0 bg-white/80">
        <div className="flex items-center gap-2">
          <User className="h-4 w-4" style={{ color: BRAND_PRIMARY }} />
          <div className="font-semibold text-[15px] text-slate-800">Dados do contato</div>
        </div>
        <button
          onClick={onClose}
          className="text-slate-400 hover:text-slate-700 p-2 hover:bg-slate-100 rounded-xl transition"
          data-testid="close-context-panel"
        >
          <X className="h-4 w-4" />
        </button>
      </header>

      <div className="flex-1 overflow-y-auto min-h-0 p-4 space-y-4">
        <div className="rounded-2xl bg-white border border-slate-200/80 p-4 shadow-sm text-center">
          <div
            className={`h-16 w-16 mx-auto rounded-2xl flex items-center justify-center font-bold text-lg text-white shadow-md overflow-hidden ${
              contact.is_lead ? "" : "bg-slate-300"
            }`}
            style={contact.is_lead ? { background: `linear-gradient(145deg, ${BRAND_PRIMARY}, ${BRAND_PRIMARY_DEEP})` } : undefined}
          >
            {contact.profile_picture_url ? (
              <img src={contact.profile_picture_url} alt={contact.name || "Contato"} className="h-full w-full object-cover" />
            ) : (
              (contact.name || "?").slice(0, 2).toUpperCase()
            )}
          </div>
          <div className="font-heading text-base font-bold text-slate-800 mt-3">{contact.name}</div>
          <div className="text-xs text-slate-500 font-mono">{contact.phone}</div>
          {contact.profile_status_text && (
            <div className="mt-2 text-xs text-slate-500 italic">"{contact.profile_status_text}"</div>
          )}
          {contact.is_lead && (
            <span
              className="inline-block mt-2 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-md font-bold border"
              style={{ color: BRAND_PRIMARY, borderColor: `${BRAND_PRIMARY}44`, background: `${BRAND_PRIMARY}14` }}
            >
              LEAD
            </span>
          )}
        </div>

        <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 px-1">Ações rápidas</div>
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={onCopyPhone}
            className="bg-white border border-slate-200/80 rounded-xl p-3 text-xs font-semibold text-slate-700 hover:bg-slate-50 transition"
          >
            Copiar telefone
          </button>
          <button
            type="button"
            onClick={onSyncProfile}
            className="bg-white border border-slate-200/80 rounded-xl p-3 text-xs font-semibold text-slate-700 hover:bg-slate-50 transition"
          >
            Atualizar perfil
          </button>
          <button
            type="button"
            onClick={onToggleMute}
            className="bg-white border border-slate-200/80 rounded-xl p-3 text-xs font-semibold text-slate-700 hover:bg-slate-50 transition"
          >
            {contact.mute_automation ? "Retomar automação" : "Pausar automação"}
          </button>
          <button
            type="button"
            onClick={() => active && onMarkPaid(active.id)}
            disabled={!active}
            className="bg-white border border-slate-200/80 rounded-xl p-3 text-xs font-semibold text-slate-700 hover:bg-slate-50 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Marcar PIX pago
          </button>
        </div>

        <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 px-1">PIX e valores</div>

        {active ? (
          <div className="bg-amber-50 border border-amber-200/80 rounded-2xl p-4 shadow-sm" data-testid="active-pix-card">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="h-8 w-8 rounded-xl bg-amber-100 border border-amber-200 flex items-center justify-center">
                  <Clock className="h-4 w-4 text-amber-600" />
                </div>
                <div className="text-[11px] uppercase tracking-wider text-amber-700 font-bold">PIX pendente</div>
              </div>
              <span className="text-[10px] text-amber-600/80">{formatRelative(active.created_at)}</span>
            </div>
            <div className="font-heading text-2xl font-bold text-amber-700 tabular-nums tracking-tight">
              {formatCurrency(active.amount)}
            </div>
            <div className="text-[11px] text-slate-500 font-mono mt-1 break-all">{active.external_id}</div>
            <button
              onClick={() => onMarkPaid(active.id)}
              data-testid="context-mark-paid-btn"
              className="mt-4 w-full font-bold text-sm py-2.5 rounded-xl transition-all inline-flex items-center justify-center gap-2 text-white shadow-md hover:opacity-95"
              style={{ backgroundColor: BRAND_PRIMARY }}
            >
              <CheckCircle2 className="h-4 w-4" /> Marcar como pago
            </button>
          </div>
        ) : (
          <div className="border border-slate-200 rounded-2xl p-4 text-center bg-white shadow-sm">
            <div className="h-10 w-10 mx-auto rounded-full bg-slate-100 flex items-center justify-center mb-2">
              <Receipt className="h-5 w-5 text-slate-400" />
            </div>
            <div className="text-sm text-slate-600">Nenhum PIX ativo</div>
            <div className="text-[11px] text-slate-400 mt-0.5">Este contato não tem transações pendentes</div>
          </div>
        )}

        <div className="grid grid-cols-3 gap-2">
          <div className="bg-white border border-slate-200/80 rounded-xl p-3 text-center shadow-sm">
            <div className="text-[9px] uppercase tracking-wider text-slate-400 font-semibold">Total</div>
            <div className="font-heading text-lg font-bold text-slate-800 tabular-nums">{s.count}</div>
          </div>
          <div className="rounded-xl p-3 text-center border shadow-sm" style={{ background: `${BRAND_PRIMARY}12`, borderColor: `${BRAND_PRIMARY}33` }}>
            <div className="text-[9px] uppercase font-semibold" style={{ color: BRAND_PRIMARY }}>
              Pagos
            </div>
            <div className="font-heading text-sm font-bold tabular-nums text-slate-800">{formatCurrency(s.total_paid)}</div>
          </div>
          <div className="bg-amber-50 border border-amber-200/60 rounded-xl p-3 text-center shadow-sm">
            <div className="text-[9px] uppercase tracking-wider text-amber-700 font-semibold">Pendente</div>
            <div className="font-heading text-sm font-bold text-amber-700 tabular-nums">{formatCurrency(s.total_pending)}</div>
          </div>
        </div>

        {s.total_recovered > 0 && (
          <div
            className="rounded-2xl p-3 flex items-center gap-3 border shadow-sm"
            style={{ background: `${BRAND_PRIMARY}10`, borderColor: `${BRAND_PRIMARY}35` }}
          >
            <div
              className="h-10 w-10 rounded-xl flex items-center justify-center border"
              style={{ background: `${BRAND_PRIMARY}22`, borderColor: `${BRAND_PRIMARY}40` }}
            >
              <TrendingUp className="h-5 w-5" style={{ color: BRAND_PRIMARY }} />
            </div>
            <div>
              <div className="text-[10px] uppercase font-bold" style={{ color: BRAND_PRIMARY }}>
                Recuperado por este lead
              </div>
              <div className="font-heading text-lg font-bold tabular-nums text-slate-800">{formatCurrency(s.total_recovered)}</div>
            </div>
          </div>
        )}

        {context.transactions.length > 0 && (
          <div>
            <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-2 px-1">Histórico</div>
            <div className="space-y-1.5">
              {context.transactions.slice(0, 10).map((tx) => (
                <div
                  key={tx.id}
                  className="bg-white hover:bg-slate-50 rounded-xl px-3 py-2.5 flex items-center justify-between gap-2 transition border border-slate-200/80 shadow-sm"
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-[11px] font-mono text-slate-500 truncate">{tx.external_id}</div>
                    <div className="text-[10px] text-slate-400 mt-0.5">{formatDateTime(tx.created_at)}</div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-heading text-sm font-bold text-slate-800 tabular-nums">{formatCurrency(tx.amount)}</div>
                    <div
                      className={`text-[10px] font-semibold uppercase tracking-wider ${
                        tx.status === "paid"
                          ? tx.recovered
                            ? "text-cp-primary"
                            : "text-cyan-600"
                          : tx.status === "pending"
                            ? "text-amber-600"
                            : "text-slate-400"
                      }`}
                    >
                      {tx.status === "paid" ? (tx.recovered ? "Recuperado" : "Pago") : tx.status === "pending" ? "Aguardando" : "Expirado"}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function ChatPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [contacts, setContacts] = useState([]);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const textRef = useRef("");
  const [sending, setSending] = useState(false);
  const [recording, setRecording] = useState(false);
  const [recordingElapsedSec, setRecordingElapsedSec] = useState(0);
  const [contextOpen, setContextOpen] = useState(false);
  const [context, setContext] = useState(null);
  const [msgSearchOpen, setMsgSearchOpen] = useState(false);
  const [msgSearch, setMsgSearch] = useState("");
  const [matchIndex, setMatchIndex] = useState(0);
  const [threadLoading, setThreadLoading] = useState(false);
  const mediaRecorderRef = useRef(null);
  const recordStartedAtRef = useRef(0);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const shouldAutoScrollRef = useRef(true);
  const prevThreadIdRef = useRef(null);
  const fileInputRef = useRef(null);
  const attachWrapRef = useRef(null);
  /** Última opção do menu de anexo: image | video | document — usado quando `File.type` vem vazio. */
  const attachIntentRef = useRef("document");
  const [attachMenuOpen, setAttachMenuOpen] = useState(false);
  /** Pré-visualização antes de enviar ficheiro (foto / vídeo / documento). */
  const [attachmentPreview, setAttachmentPreview] = useState(null);
  const attachmentPreviewRef = useRef(null);
  const prevSelectedIdRef = useRef(undefined);
  const selectedRef = useRef(null);
  const contactsRef = useRef([]);
  /** Incrementado em cada troca de conversa — evita que um GET lento sobrescreva a conversa já aberta. */
  const activeThreadSeqRef = useRef(0);
  const matchRefs = useRef([]);
  const { user } = useAuth();
  const [tenantInfo, setTenantInfo] = useState(null);
  /** Esconde lista de conversas / rail WA / painel lateral — só a coluna do chat (md+). */
  const [chatImmersive, setChatImmersive] = useState(false);
  const [operatorWaAvatar, setOperatorWaAvatar] = useState(null);
  /** Multi-WhatsApp: inbox e envio manual. */
  const [waInstances, setWaInstances] = useState([]);
  const waInstancesRef = useRef([]);
  const [inboxLayout, setInboxLayout] = useState("unified"); // unified | separated
  const [listFilterInstanceId, setListFilterInstanceId] = useState("");
  /** Modo separado: filtra qual grupo de instância está visível (null = todos os grupos). */
  const [separatedFocusInstanceId, setSeparatedFocusInstanceId] = useState(null);
  const [showScrollToTop, setShowScrollToTop] = useState(false);
  const [chatMenuOpen, setChatMenuOpen] = useState(false);
  const chatMenuWrapRef = useRef(null);
  const [mediaViewer, setMediaViewer] = useState(null); // { type: "image"|"video", src, name }
  const [mediaZoom, setMediaZoom] = useState(1);
  const chatParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const isGlobalChatFull = chatParams.get("chat_full") === "1";
  const requestedContactId = chatParams.get("contact_id") || "";
  const setGlobalChatFullscreen = useCallback(
    (enabled) => {
      const params = new URLSearchParams(location.search);
      if (enabled) params.set("chat_full", "1");
      else params.delete("chat_full");
      const search = params.toString();
      navigate(
        { pathname: location.pathname, search: search ? `?${search}` : "" },
        { replace: true },
      );
    },
    [location.pathname, location.search, navigate],
  );
  const exitGlobalChatFullscreen = useCallback(() => {
    setGlobalChatFullscreen(false);
    setChatImmersive(false);
  }, [setGlobalChatFullscreen]);

  useEffect(() => {
    contactsRef.current = contacts;
  }, [contacts]);

  const meLabel = user && typeof user === "object" ? (user.name || user.email || "Conta") : "Conta";
  const meInitials =
    user && typeof user === "object"
      ? String(user.name || user.email || "?")
          .replace(/@.*/, "")
          .trim()
          .slice(0, 2)
          .toUpperCase() || "?"
      : "?";
  const selectedOnlineNow = isContactOnlineNow(selected);

  const clearAttachmentPreview = useCallback(() => {
    setAttachmentPreview((old) => {
      if (old?.previewUrl) URL.revokeObjectURL(old.previewUrl);
      return null;
    });
    if (fileInputRef.current) fileInputRef.current.value = "";
  }, []);

  const discardAttachmentPreview = useCallback(() => {
    setText("");
    clearAttachmentPreview();
  }, [clearAttachmentPreview]);

  const closeConversation = useCallback(() => {
    activeThreadSeqRef.current += 1;
    setSelected(null);
    setMessages([]);
    setContext(null);
    setThreadLoading(false);
    setChatMenuOpen(false);
  }, []);

  const openMediaViewer = useCallback((media) => {
    if (!media?.src || !media?.type) return;
    setMediaZoom(1);
    setMediaViewer(media);
  }, []);

  const closeMediaViewer = useCallback(() => {
    setMediaViewer(null);
    setMediaZoom(1);
  }, []);
  const changeMediaZoom = useCallback((delta) => {
    setMediaZoom((z) => Math.max(0.5, Math.min(4, +(z + delta).toFixed(2))));
  }, []);

  useEffect(() => {
    textRef.current = text;
  }, [text]);

  useEffect(() => { selectedRef.current = selected; }, [selected]);

  useEffect(() => {
    if (!selected && chatImmersive) {
      setChatImmersive(false);
      if (isGlobalChatFull) setGlobalChatFullscreen(false);
    }
    if (!isGlobalChatFull && chatImmersive) {
      setChatImmersive(false);
    }
  }, [chatImmersive, isGlobalChatFull, selected, setGlobalChatFullscreen]);

  useEffect(() => {
    if (!isGlobalChatFull) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        exitGlobalChatFullscreen();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [exitGlobalChatFullscreen, isGlobalChatFull]);

  useEffect(() => {
    if (!mediaViewer) return undefined;
    if (mediaViewer.type === "video") {
      const inlineVideos = messagesContainerRef.current?.querySelectorAll("video");
      inlineVideos?.forEach((videoEl) => {
        if (!videoEl.paused) videoEl.pause();
      });
    }
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKeyDown = (event) => {
      if (event.key === "Escape") closeMediaViewer();
      if (mediaViewer.type === "image") {
        if (event.key === "+" || event.key === "=") setMediaZoom((z) => Math.min(4, +(z + 0.2).toFixed(2)));
        if (event.key === "-") setMediaZoom((z) => Math.max(0.5, +(z - 0.2).toFixed(2)));
        if (event.key === "0") setMediaZoom(1);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [closeMediaViewer, mediaViewer]);

  useEffect(() => {
    waInstancesRef.current = waInstances;
  }, [waInstances]);

  useEffect(() => {
    if (!chatMenuOpen) return undefined;
    const onDoc = (e) => {
      if (chatMenuWrapRef.current && !chatMenuWrapRef.current.contains(e.target)) {
        setChatMenuOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === "Escape") setChatMenuOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [chatMenuOpen]);

  useEffect(() => {
    attachmentPreviewRef.current = attachmentPreview;
  }, [attachmentPreview]);

  useEffect(() => {
    return () => {
      const p = attachmentPreviewRef.current;
      if (p?.previewUrl) URL.revokeObjectURL(p.previewUrl);
    };
  }, []);

  useEffect(() => {
    if (!attachmentPreview) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") discardAttachmentPreview();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [attachmentPreview, discardAttachmentPreview]);

  useEffect(() => {
    const id = selected?.id;
    if (prevSelectedIdRef.current !== undefined && prevSelectedIdRef.current !== id) {
      clearAttachmentPreview();
    }
    prevSelectedIdRef.current = id;
  }, [selected?.id, clearAttachmentPreview]);

  useEffect(() => {
    if (!attachMenuOpen) return undefined;
    const close = (e) => {
      if (attachWrapRef.current && !attachWrapRef.current.contains(e.target)) setAttachMenuOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [attachMenuOpen]);

  useEffect(() => {
    if (!recording) return undefined;
    const tick = () => {
      setRecordingElapsedSec(Math.max(0, Math.floor((Date.now() - recordStartedAtRef.current) / 1000)));
    };
    tick();
    const id = setInterval(tick, 250);
    return () => clearInterval(id);
  }, [recording]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/tenant");
        if (cancelled) return;
        setTenantInfo(data);
        const url = data?.whatsapp_profile_picture_url;
        if (url) setOperatorWaAvatar(url);
        else if (data?.evolution_status === "connected") {
          try {
            const { data: d2 } = await api.post("/tenant/sync-whatsapp-profile");
            if (!cancelled && d2?.whatsapp_profile_picture_url) setOperatorWaAvatar(d2.whatsapp_profile_picture_url);
          } catch {
            /* ignore */
          }
        }
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const loadWaInstances = useCallback(async () => {
    try {
      const { data } = await api.get("/whatsapp/instances");
      setWaInstances(Array.isArray(data?.instances) ? data.instances : []);
    } catch {
      setWaInstances([]);
    }
  }, []);

  const loadWaInstancesRef = useRef(loadWaInstances);
  useEffect(() => {
    loadWaInstancesRef.current = loadWaInstances;
  }, [loadWaInstances]);

  useEffect(() => {
    void loadWaInstances();
  }, [loadWaInstances]);

  useEffect(() => {
    if (inboxLayout === "separated") setListFilterInstanceId("");
    setSeparatedFocusInstanceId(null);
  }, [inboxLayout]);

  const loadContacts = useCallback(async () => {
    const params = { filter };
    if (inboxLayout === "unified" && listFilterInstanceId) {
      params.instance_id = listFilterInstanceId;
    }
    const { data } = await api.get("/chat/contacts", { params });
    setContacts(data);
  }, [filter, inboxLayout, listFilterInstanceId]);

  const loadContext = useCallback(async (contactId) => {
    try {
      const { data } = await api.get(`/chat/contacts/${contactId}/context`);
      setContext(data);
    } catch {}
  }, []);

  const loadMessages = useCallback(async (contactId, options = {}) => {
    const { silent = false } = options;
    const seq = ++activeThreadSeqRef.current;
    if (!silent) setThreadLoading(true);
    try {
      const { data } = await api.get(`/chat/contacts/${contactId}/messages`, { timeout: 20000 });
      if (seq !== activeThreadSeqRef.current) return;
      setMessages(data.messages);
      setSelected(data.contact);
      const wasInList = data.contact?.id ? contactsRef.current.some((x) => x.id === data.contact.id) : true;
      setContacts((prev) => {
        if (!data.contact?.id) return prev;
        if (prev.some((x) => x.id === data.contact.id)) {
          return prev.map((x) => (x.id === data.contact.id ? { ...x, ...data.contact } : x));
        }
        return prev;
      });
      if (data.contact?.id && !wasInList) void loadContacts();
      // Best-effort profile sync to enrich avatar/photo in chat.
      api.post(`/chat/contacts/${contactId}/profile`)
        .then((res) => {
          if (seq !== activeThreadSeqRef.current) return;
          const updated = res?.data?.contact;
          if (!updated) return;
          setSelected((prev) => (prev?.id === updated.id ? updated : prev));
          setContacts((prev) => prev.map((c) => (c.id === updated.id ? { ...c, ...updated } : c)));
        })
        .catch(() => {});
      setMsgSearchOpen(false);
      setMsgSearch("");
      setMatchIndex(0);
      api.post(`/chat/contacts/${contactId}/mark-read`).catch(() => {});
      if (seq !== activeThreadSeqRef.current) return;
      loadContext(contactId);
    } catch (e) {
      if (!silent) {
        toast.error(e?.response?.data?.detail || "Falha ao carregar conversa");
      }
    } finally {
      if (seq === activeThreadSeqRef.current) setThreadLoading(false);
    }
  }, [loadContacts, loadContext]);

  const openConversation = useCallback(
    (contact) => {
      if (!contact?.id) return;
      if (selectedRef.current?.id === contact.id) return;
      // Instant UI feedback: switch thread immediately, fetch messages in background.
      setSelected(contact);
      setMessages([]);
      setContext(null);
      setThreadLoading(true);
      setMsgSearchOpen(false);
      setMsgSearch("");
      setMatchIndex(0);
      shouldAutoScrollRef.current = true;
      void loadMessages(contact.id);
    },
    [loadMessages],
  );

  const syncSelectedProfile = useCallback(async () => {
    const current = selectedRef.current;
    if (!current?.id) return;
    try {
      const { data } = await api.post(`/chat/contacts/${current.id}/profile`);
      const updated = data?.contact;
      if (updated) {
        setSelected((prev) => (prev?.id === updated.id ? updated : prev));
        setContacts((prev) => prev.map((c) => (c.id === updated.id ? { ...c, ...updated } : c)));
      }
      toast.success("Perfil atualizado");
    } catch {
      toast.error("Não foi possível atualizar o perfil");
    }
  }, []);

  const upsertContactWithMessage = useCallback((msg, currentSel) => {
    let selectedListHeadPatch = null;
    setContacts((prev) => {
      const copy = [...prev];
      const idx = copy.findIndex((c) => c.id === msg.contact_id);
      if (idx >= 0) {
        const c = {
          ...copy[idx],
          last_message_at: msg.created_at,
          last_message_preview: lastPreviewFromMessage(msg) || msg.text || `[${msg.type}]`,
        };
        if (msg.instance?.id) {
          c.last_message_instance_id = msg.instance.id;
          c.last_message_instance_name = msg.instance.display_name ?? null;
        } else if (msg.instance_id) {
          c.last_message_instance_id = msg.instance_id;
          const row = waInstancesRef.current.find((w) => w.id === msg.instance_id);
          if (row?.display_name) c.last_message_instance_name = row.display_name;
        }
        if (msg.direction === "in" && msg.instance_id && !c.conversation_instance_id) {
          c.conversation_instance_id = msg.instance_id;
          c.conversation_instance_name =
            msg.instance?.display_name ?? waInstancesRef.current.find((w) => w.id === msg.instance_id)?.display_name ?? null;
        }
        const sameThread =
          !!currentSel && (currentSel.id === msg.contact_id || samePhoneContact(currentSel, c));
        if (msg.direction === "in" && !sameThread) {
          c.unread_count = (c.unread_count || 0) + 1;
        }
        if (msg.direction === "out" && sameThread) {
          c.unread_count = 0;
        }
        copy.splice(idx, 1);
        copy.unshift(c);
        if (sameThread) {
          selectedListHeadPatch = {
            last_message_at: c.last_message_at,
            last_message_preview: c.last_message_preview,
            last_message_instance_id: c.last_message_instance_id,
            last_message_instance_name: c.last_message_instance_name,
            conversation_instance_id: c.conversation_instance_id,
            conversation_instance_name: c.conversation_instance_name,
            unread_count: c.unread_count,
          };
        }
        return copy;
      }
      loadContacts();
      return prev;
    });
    if (selectedListHeadPatch) {
      setSelected((s) => (s && s.id === msg.contact_id ? { ...s, ...selectedListHeadPatch } : s));
    }
  }, [loadContacts]);

  const appendMessageIfSelected = useCallback((msg, currentSel) => {
    const incomingContact = contactsRef.current.find((c) => c.id === msg.contact_id);
    const incomingByPhone = msg?.phone ? { phone: msg.phone } : null;
    const sameThread =
      !!currentSel &&
      (msg.contact_id === currentSel.id ||
        samePhoneContact(currentSel, incomingContact) ||
        samePhoneContact(currentSel, incomingByPhone));
    if (sameThread) {
      setMessages((prev) => {
        if (prev.some((m) => m.id === msg.id)) return prev;
        if (msg.evolution_id) {
          const dupIdx = prev.findIndex(
            (m) =>
              m.evolution_id &&
              m.evolution_id === msg.evolution_id &&
              m.direction === "out",
          );
          if (dupIdx >= 0) {
            const old = prev[dupIdx];
            const oldMedia = old.media_base64 != null ? String(old.media_base64).trim() : "";
            const newMedia = msg.media_base64 != null ? String(msg.media_base64).trim() : "";
            if (!oldMedia && newMedia) {
              const next = [...prev];
              next[dupIdx] = { ...msg, id: old.id };
              return next;
            }
            return prev;
          }
        }
        return [...prev, msg];
      });
      const readId = currentSel?.id || msg.contact_id;
      api.post(`/chat/contacts/${readId}/mark-read`).catch(() => {});
    }
  }, []);

  const handleWsEvent = useCallback((event) => {
    if (event.event === "message") {
      const msg = event.message;
      const currentSel = selectedRef.current;
      upsertContactWithMessage(msg, currentSel);
      appendMessageIfSelected(msg, currentSel);
    }
    if (event.event === "contact_muted") {
      setContacts((prev) => prev.map((c) => c.id === event.contact_id ? { ...c, mute_automation: true } : c));
      const currentSel = selectedRef.current;
      if (currentSel && currentSel.id === event.contact_id) {
        setSelected((s) => s ? { ...s, mute_automation: true } : s);
      }
    }
    if (event.event === "message_status") {
      const currentSel = selectedRef.current;
      if (currentSel && event.contact_id === currentSel.id) {
        setMessages((prev) =>
          prev.map((m) =>
            m.id === event.message_id || (event.evolution_id && m.evolution_id === event.evolution_id)
              ? { ...m, delivery_status: event.delivery_status }
              : m
          )
        );
      }
    }
  }, [appendMessageIfSelected, upsertContactWithMessage]);

  const loadContactsRef = useRef(loadContacts);
  useEffect(() => {
    loadContactsRef.current = loadContacts;
  }, [loadContacts]);

  const refreshOpenThread = useCallback(async () => {
    const id = selectedRef.current?.id;
    if (!id) return;
    try {
      const { data } = await api.get(`/chat/contacts/${id}/messages`);
      if (selectedRef.current?.id !== id) return;
      setMessages(data.messages);
      setSelected((prev) => (prev?.id === id ? { ...prev, ...data.contact } : prev));
    } catch {
      /* ignore */
    }
  }, []);

  const refreshOpenThreadRef = useRef(refreshOpenThread);
  useEffect(() => {
    refreshOpenThreadRef.current = refreshOpenThread;
  }, [refreshOpenThread]);

  const onWsOpen = useCallback(() => {
    void loadContactsRef.current();
    void refreshOpenThreadRef.current();
    void loadWaInstancesRef.current?.();
  }, []);

  const [wsConnected, setWsConnected] = useState(false);

  useChatWebSocket({
    onEvent: handleWsEvent,
    onOpen: onWsOpen,
    onConnectionChange: setWsConnected,
  });

  useEffect(() => {
    if (wsConnected) return;
    const tick = () => {
      void loadContactsRef.current();
      void refreshOpenThreadRef.current();
    };
    tick();
    const intervalMs = () =>
      typeof document !== "undefined" && document.visibilityState === "visible" ? 2500 : 5000;
    let intervalId = null;
    const arm = () => {
      if (intervalId) clearInterval(intervalId);
      intervalId = setInterval(tick, intervalMs());
    };
    arm();
    const onVis = () => {
      tick();
      arm();
    };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      if (intervalId) clearInterval(intervalId);
    };
  }, [wsConnected]);

  useEffect(() => { loadContacts(); }, [loadContacts]);

  const handleMessagesScroll = useCallback(() => {
    const el = messagesContainerRef.current;
    if (!el) return;
    const distanceToBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    shouldAutoScrollRef.current = distanceToBottom <= 80;
    setShowScrollToTop(el.scrollTop > 260);
  }, []);

  useEffect(() => {
    if (msgSearchOpen) return;
    const threadChanged = prevThreadIdRef.current !== selected?.id;
    prevThreadIdRef.current = selected?.id;
    if (!threadChanged && !shouldAutoScrollRef.current) return;
    const el = messagesContainerRef.current;
    if (!el) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
      return;
    }
    requestAnimationFrame(() => {
      el.scrollTo({ top: el.scrollHeight, behavior: threadChanged ? "auto" : "smooth" });
      shouldAutoScrollRef.current = true;
    });
  }, [messages, selected?.id, msgSearchOpen]);

  const filteredContacts = useMemo(
    () =>
      contacts.filter(
        (c) =>
          !search ||
          (c.name || "").toLowerCase().includes(search.toLowerCase()) ||
          (c.phone || "").includes(search),
      ),
    [contacts, search],
  );

  useEffect(() => {
    if (!requestedContactId || !contacts.length) return;
    const target = contacts.find((c) => c.id === requestedContactId);
    if (!target) return;
    if (selectedRef.current?.id !== target.id) {
      openConversation(target);
    }
    const params = new URLSearchParams(location.search);
    params.delete("contact_id");
    const nextSearch = params.toString();
    navigate(
      { pathname: location.pathname, search: nextSearch ? `?${nextSearch}` : "" },
      { replace: true },
    );
  }, [contacts, location.pathname, location.search, navigate, openConversation, requestedContactId]);

  const contactSections = useMemo(() => {
    if (inboxLayout !== "separated") return null;
    const m = new Map();
    for (const c of filteredContacts) {
      const kid = c.last_message_instance_id || "__none";
      const label = c.last_message_instance_name || "Sem WhatsApp";
      if (!m.has(kid)) m.set(kid, { kid, label, contacts: [] });
      m.get(kid).contacts.push(c);
    }
    const arr = [...m.values()];
    arr.sort((a, b) => (a.label || "").localeCompare(b.label || ""));
    for (const s of arr) {
      s.contacts.sort((a, b) => {
        const ta = new Date(a.last_message_at || 0).getTime();
        const tb = new Date(b.last_message_at || 0).getTime();
        return tb - ta;
      });
    }
    return arr;
  }, [inboxLayout, filteredContacts]);

  const visibleContactSections = useMemo(() => {
    if (!contactSections) return null;
    if (!separatedFocusInstanceId) return contactSections;
    return contactSections.filter((s) => s.kid === separatedFocusInstanceId);
  }, [contactSections, separatedFocusInstanceId]);

  const pickInboxAll = useCallback(() => {
    setListFilterInstanceId("");
    setSeparatedFocusInstanceId(null);
  }, []);

  const pickInboxInstance = useCallback(
    (instanceId) => {
      if (inboxLayout === "unified") {
        setListFilterInstanceId(instanceId);
      } else {
        setSeparatedFocusInstanceId(instanceId);
      }
    },
    [inboxLayout],
  );

  const showWaInstanceRail = waInstances.length > 0;

  // Message matches for in-chat search
  const matchedIndexes = useMemo(() => {
    if (!msgSearch || !msgSearch.trim()) return [];
    const q = msgSearch.toLowerCase();
    return messages.map((m, i) => ((m.text || "").toLowerCase().includes(q) ? i : -1)).filter((i) => i >= 0);
  }, [msgSearch, messages]);

  useEffect(() => {
    if (matchedIndexes.length === 0 || !msgSearchOpen) return;
    const targetIdx = matchedIndexes[matchIndex] ?? matchedIndexes[0];
    const el = matchRefs.current[targetIdx];
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [matchIndex, matchedIndexes, msgSearchOpen]);

  const navigateMatch = (dir) => {
    if (matchedIndexes.length === 0) return;
    setMatchIndex((i) => (i + dir + matchedIndexes.length) % matchedIndexes.length);
  };

  /** O backend escolhe sempre a instância da conversa (origem no WhatsApp); o cliente não envia override. */
  const manualSendExtra = useCallback(() => ({}), []);

  const sendText = async () => {
    if (attachmentPreview) return;
    if (!text.trim() || !selected) return;
    const pendingId = `pending-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const pendingPayload = text;
    setMessages((prev) => [
      ...prev,
      {
        id: pendingId,
        contact_id: selected.id,
        type: "text",
        text: pendingPayload,
        created_at: new Date().toISOString(),
        _local_ui: "sending",
        direction: "out",
      },
    ]);
    const traceId = newTraceId("msg");
    setSending(true);
    try {
      const { data } = await api.post(`/chat/contacts/${selected.id}/send`, {
        type: "text",
        text,
        correlation_id: traceId,
        ...manualSendExtra(),
      });
      setMessages((prev) => prev.filter((m) => m.id !== pendingId));
      const currentSel = selectedRef.current;
      if (data?.id) {
        upsertContactWithMessage(data, currentSel);
        appendMessageIfSelected(data, currentSel);
        if (data.delivery_status === "failed") {
          toast.error(data.send_error || "Falha no envio da mensagem");
        }
      } else if (currentSel?.id) {
        // Evita falso "enviado" sem confirmação canônica do backend.
        await loadMessages(currentSel.id, { silent: true });
      }
      setText("");
    } catch (e) {
      setMessages((prev) => prev.filter((m) => m.id !== pendingId));
      toast.error(e.response?.data?.detail || "Falha ao enviar");
    }
    setSending(false);
  };

  const openAttachPicker = (accept, intent) => {
    attachIntentRef.current = intent || "document";
    setAttachMenuOpen(false);
    const el = fileInputRef.current;
    if (!el) return;
    el.accept = accept || "";
    el.value = "";
    requestAnimationFrame(() => el.click());
  };

  const prepareAttachmentPreview = useCallback((file) => {
    if (!file || !selectedRef.current) return;
    if (file.size > MAX_CHAT_FILE_BYTES) {
      toast.error("Ficheiro demasiado grande (máx. ~9 MB). Comprima ou escolha outro.");
      return;
    }
    const menuIntent = attachIntentRef.current || "document";
    const { kind, mimetype, name } = inferChatMedia(file, menuIntent);
    let previewUrl = null;
    if (kind === "image" || kind === "video") {
      previewUrl = URL.createObjectURL(file);
    }
    setAttachmentPreview((old) => {
      if (old?.previewUrl) URL.revokeObjectURL(old.previewUrl);
      return { file, kind, mimetype, name, previewUrl };
    });
  }, []);

  const confirmSendAttachment = useCallback(async () => {
    const p = attachmentPreview;
    if (!p?.file || !selectedRef.current) return;
    const { file, kind, mimetype, name } = p;
    const sel = selectedRef.current;
    const caption = (textRef.current || "").trim();
    const pendingId = `pending-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const pendingText = caption || (kind === "image" ? "Imagem" : kind === "video" ? "Vídeo" : "Documento");
    const traceId = newTraceId("msg");
    setMessages((prev) => [
      ...prev,
      {
        id: pendingId,
        contact_id: sel.id,
        type: kind,
        text: pendingText,
        created_at: new Date().toISOString(),
        _local_ui: "sending",
        direction: "out",
      },
    ]);
    setSending(true);
    try {
      const dataUrl = await new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(String(r.result));
        r.onerror = reject;
        r.readAsDataURL(file);
      });
      const comma = dataUrl.indexOf(",");
      const base64 = comma >= 0 ? dataUrl.slice(comma + 1) : "";
      if (!base64.trim()) throw new Error("empty_base64");

      const { data } = await api.post(
        `/chat/contacts/${sel.id}/send`,
        {
          type: kind,
          media_base64: base64,
          mimetype,
          filename: name,
          caption,
          correlation_id: traceId,
          ...manualSendExtra(),
        },
        { timeout: 120000 },
      );
      setMessages((prev) => prev.filter((m) => m.id !== pendingId));
      const currentSel = selectedRef.current;
      if (data?.id) {
        upsertContactWithMessage(data, currentSel);
        appendMessageIfSelected(data, currentSel);
        if (data.delivery_status === "failed") {
          toast.error(data.send_error || "Falha no envio da mensagem");
        }
      } else if (currentSel?.id) {
        await loadMessages(currentSel.id, { silent: true });
      }
      discardAttachmentPreview();
    } catch (e) {
      setMessages((prev) => prev.filter((m) => m.id !== pendingId));
      if (e?.message === "empty_base64") {
        toast.error("Não foi possível ler o ficheiro. Tente outro formato.");
      } else {
        toastApiError(
          e,
          kind === "image" ? "Falha ao enviar imagem" : kind === "video" ? "Falha ao enviar vídeo" : "Falha ao enviar documento",
        );
      }
    }
    setSending(false);
  }, [attachmentPreview, appendMessageIfSelected, upsertContactWithMessage, discardAttachmentPreview, manualSendExtra, loadMessages]);

  const startRecording = async () => {
    if (attachmentPreviewRef.current) discardAttachmentPreview();
    if (!selected) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream, { mimeType: "audio/webm" });
      const chunks = [];
      mr.ondataavailable = (e) => { if (e.data.size) chunks.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunks, { type: "audio/webm" });
        const durationSec = Math.max(1, Math.round((Date.now() - recordStartedAtRef.current) / 1000));
        const pendingId = `pending-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        setMessages((prev) => [
          ...prev,
          {
            id: pendingId,
            contact_id: selectedRef.current?.id,
            type: "audio",
            text: "Áudio",
            created_at: new Date().toISOString(),
            _local_ui: "sending",
            direction: "out",
          },
        ]);
        const traceId = newTraceId("msg");
        const base64 = await new Promise((resolve, reject) => {
          const r = new FileReader();
          r.onload = () => resolve(String(r.result).split(",")[1]);
          r.onerror = reject;
          r.readAsDataURL(blob);
        });
        try {
          const { data } = await api.post(
            `/chat/contacts/${selected.id}/send`,
            {
              type: "audio",
              media_base64: base64,
              mimetype: "audio/webm",
              audio_duration_sec: durationSec,
              correlation_id: traceId,
              ...manualSendExtra(),
            },
            { timeout: 120000 },
          );
          setMessages((prev) => prev.filter((m) => m.id !== pendingId));
          const currentSel = selectedRef.current;
          if (data?.id) {
            upsertContactWithMessage(data, currentSel);
            appendMessageIfSelected(data, currentSel);
            if (data.delivery_status === "failed") {
              toast.error(data.send_error || "Falha no envio da mensagem");
            }
          } else if (currentSel?.id) {
            await loadMessages(currentSel.id, { silent: true });
          }
        } catch (e) {
          setMessages((prev) => prev.filter((m) => m.id !== pendingId));
          toast.error(e.response?.data?.detail || "Falha no áudio");
        }
      };
      recordStartedAtRef.current = Date.now();
      mr.start();
      mediaRecorderRef.current = mr;
      setRecording(true);
    } catch {
      toast.error("Não foi possível acessar o microfone");
    }
  };

  const stopRecording = () => { mediaRecorderRef.current?.stop(); setRecording(false); };

  const toggleMute = async () => {
    if (!selected) return;
    try {
      const endpoint = selected.mute_automation ? "unmute" : "mute";
      await api.post(`/chat/contacts/${selected.id}/${endpoint}`);
      const newMute = !selected.mute_automation;
      setSelected({ ...selected, mute_automation: newMute });
      setContacts((prev) => prev.map((c) => c.id === selected.id ? { ...c, mute_automation: newMute } : c));
      toast.success(newMute ? "Automação pausada para este lead" : "Automação retomada");
    } catch {
      toast.error("Falha ao alterar automação");
    }
  };

  const markPaid = async (txId) => {
    try {
      await api.post(`/simulate/pix-paid/${txId}`);
      toast.success("PIX marcado como pago");
      loadContext(selected.id);
    } catch {
      toast.error("Falha");
    }
  };

  const copyPhone = async () => {
    const current = selectedRef.current;
    if (!current?.phone) return;
    try {
      await navigator.clipboard.writeText(current.phone);
      toast.success("Telefone copiado");
    } catch {
      toast.error("Não foi possível copiar o telefone");
    }
  };

  // Build rendered messages with date separators
  const renderedMessages = [];
  let lastDate = null;
  let lastDirection = null;
  matchRefs.current = [];
  messages.forEach((m, i) => {
    const d = new Date(m.created_at).toDateString();
    if (d !== lastDate) {
      const today = new Date().toDateString();
      const y = new Date(); y.setDate(y.getDate() - 1);
      const label = d === today ? "HOJE" : d === y.toDateString() ? "ONTEM" : new Date(m.created_at).toLocaleDateString("pt-BR", { day: "2-digit", month: "long" }).toUpperCase();
      renderedMessages.push(<DateSeparator key={`sep-${i}`} label={label} />);
      lastDate = d;
      lastDirection = null;
    }
    const showTail = lastDirection !== m.direction;
    const matched = matchedIndexes.includes(i);
    const isCurrent = matched && matchedIndexes[matchIndex] === i;
    renderedMessages.push(
      <MessageBubble
        key={m.id}
        msg={m}
        contactId={selected?.id}
        showTail={showTail}
        query={msgSearch}
        matched={matched}
        isCurrentMatch={isCurrent}
        innerRef={(el) => { matchRefs.current[i] = el; }}
        leadAvatar={selected?.profile_picture_url}
        leadName={selected?.name}
        operatorInitials={meInitials}
        operatorWaAvatar={operatorWaAvatar}
        onOpenMedia={openMediaViewer}
      />
    );
    lastDirection = m.direction;
  });

  const showContextPanel = contextOpen && selected && !chatImmersive && !isGlobalChatFull;

  // `chat_full=1` keeps the full chat workspace (inbox + contacts + thread) visible.
  const immersiveLayout = Boolean(chatImmersive && selected && !isGlobalChatFull);

  const inboxAllActive =
    (inboxLayout === "unified" && !listFilterInstanceId) || (inboxLayout === "separated" && !separatedFocusInstanceId);
  const inboxInstanceActive = (id) =>
    (inboxLayout === "unified" && listFilterInstanceId === id) || (inboxLayout === "separated" && separatedFocusInstanceId === id);

  return (
    <div className="chat-page-theme-light flex min-h-0 min-w-0 flex-1 flex-col">
      <div
        className={`relative grid min-h-0 flex-1 grid-cols-1 overflow-hidden ${
          immersiveLayout
            ? "md:grid-cols-1 lg:grid-cols-1"
            : `${showWaInstanceRail
                ? "md:grid-cols-[220px,320px,minmax(0,1fr)]"
                : "md:grid-cols-[320px,minmax(0,1fr)]"} ${
                showContextPanel
                  ? showWaInstanceRail
                    ? "lg:grid-cols-[220px,320px,minmax(0,1fr),300px]"
                    : "lg:grid-cols-[320px,minmax(0,1fr),320px]"
                  : ""
              }`
        }`}
        style={{ background: LIGHT_BG }}
        data-testid="chat-page"
      >
        {showWaInstanceRail && !immersiveLayout && (
          <aside
            className="hidden min-h-0 w-full min-w-0 flex-col border-r border-slate-200/90 bg-slate-100/90 md:flex"
            aria-label="Números WhatsApp"
            data-testid="chat-wa-instance-rail"
          >
            <div className="shrink-0 border-b border-slate-200/80 px-2 py-3">
              <div className="mb-2 flex items-center justify-between gap-2 px-1">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Inbox</p>
                <button
                  type="button"
                  onClick={() => {
                    if (isGlobalChatFull) {
                      exitGlobalChatFullscreen();
                      return;
                    }
                    setGlobalChatFullscreen(true);
                    setChatImmersive(false);
                  }}
                  data-testid="chat-inbox-fullscreen-toggle"
                  className={`flex items-center gap-1 rounded-md border px-1.5 py-1 text-[10px] font-semibold transition ${
                    isGlobalChatFull
                      ? "border-teal-200 bg-teal-50 text-teal-700"
                      : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
                  }`}
                  title={isGlobalChatFull ? "Sair da tela cheia do chat" : "Tela cheia do chat"}
                >
                  {isGlobalChatFull ? <Minimize2 className="h-3 w-3" /> : <Maximize2 className="h-3 w-3" />}
                  {isGlobalChatFull ? "Sair" : "Tela cheia"}
                </button>
              </div>
              <div className="flex rounded-xl border border-slate-200/90 bg-white p-0.5 shadow-sm">
                <button
                  type="button"
                  data-testid="chat-inbox-unified"
                  onClick={() => setInboxLayout("unified")}
                  className={`flex-1 rounded-lg px-2 py-2 text-[11px] font-semibold transition ${
                    inboxLayout === "unified" ? "bg-slate-800 text-white shadow-sm" : "text-slate-600 hover:bg-slate-50"
                  }`}
                >
                  Juntos
                </button>
                <button
                  type="button"
                  data-testid="chat-inbox-separated"
                  onClick={() => setInboxLayout("separated")}
                  className={`flex-1 rounded-lg px-2 py-2 text-[11px] font-semibold transition ${
                    inboxLayout === "separated" ? "bg-slate-800 text-white shadow-sm" : "text-slate-600 hover:bg-slate-50"
                  }`}
                >
                  Separado
                </button>
              </div>
            </div>
            <div className="flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto px-1.5 py-2">
              <button
                type="button"
                onClick={pickInboxAll}
                data-testid="chat-rail-all"
                className={`flex w-full items-center gap-2 rounded-xl px-2 py-2.5 text-left text-[13px] font-semibold transition ${
                  inboxAllActive ? "bg-white text-slate-900 shadow-sm ring-1 ring-slate-200" : "text-slate-600 hover:bg-white/70"
                }`}
              >
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-slate-200 text-slate-600">
                  <MessagesSquare className="h-4 w-4" />
                </span>
                <span className="min-w-0 truncate">Todos</span>
              </button>
              {waInstances.map((w) => (
                <button
                  key={w.id}
                  type="button"
                  onClick={() => pickInboxInstance(w.id)}
                  data-testid={`chat-rail-instance-${w.id}`}
                  title={w.phone || w.display_name}
                  className={`flex w-full items-center gap-2 rounded-xl px-2 py-2.5 text-left text-[13px] transition ${
                    inboxInstanceActive(w.id) ? "bg-white text-slate-900 shadow-sm ring-1 ring-slate-200" : "text-slate-700 hover:bg-white/70"
                  }`}
                >
                  <span className="relative flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white shadow-inner ring-1 ring-slate-200/80">
                    <Smartphone className="h-4 w-4" style={{ color: w.status === "connected" ? BRAND_PRIMARY : "#94a3b8" }} />
                    {w.status === "connected" ? (
                      <span
                        className="absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full ring-2 ring-slate-100"
                        style={{ backgroundColor: BRAND_PRIMARY }}
                        aria-hidden
                      />
                    ) : null}
                  </span>
                  <span className="min-w-0 flex-1 truncate font-medium">{w.display_name}</span>
                </button>
              ))}
            </div>
          </aside>
        )}

        <aside
          className={`chat-sidebar-shell flex flex-col min-h-0 bg-[#f8f9fb] border-r border-slate-200/90 md:w-[320px] md:min-w-[320px] md:max-w-[320px] ${immersiveLayout ? "hidden" : ""} ${selected ? "hidden md:flex" : "flex"}`}
        >
          {showWaInstanceRail && (
            <div className="shrink-0 border-b border-slate-200/70 bg-[#f8f9fb] px-3 py-2 md:hidden" data-testid="chat-wa-instance-chips">
              <div className="mb-2 flex rounded-xl border border-slate-200/90 bg-white p-0.5 shadow-sm">
                <button
                  type="button"
                  onClick={() => setInboxLayout("unified")}
                  className={`flex-1 rounded-lg px-2 py-1.5 text-[11px] font-semibold ${
                    inboxLayout === "unified" ? "bg-slate-800 text-white" : "text-slate-600"
                  }`}
                >
                  Juntos
                </button>
                <button
                  type="button"
                  onClick={() => setInboxLayout("separated")}
                  className={`flex-1 rounded-lg px-2 py-1.5 text-[11px] font-semibold ${
                    inboxLayout === "separated" ? "bg-slate-800 text-white" : "text-slate-600"
                  }`}
                >
                  Separado
                </button>
              </div>
              <div className="-mx-1 flex flex-wrap gap-1.5 pb-1 pt-0.5">
                <button
                  type="button"
                  onClick={pickInboxAll}
                  data-testid="chat-chip-all"
                  className={`rounded-full border px-3 py-1.5 text-[12px] font-semibold ${
                    inboxAllActive ? "border-slate-800 bg-slate-800 text-white" : "border-slate-200 bg-white text-slate-600"
                  }`}
                >
                  Todos
                </button>
                {waInstances.map((w) => (
                  <button
                    key={w.id}
                    type="button"
                    onClick={() => pickInboxInstance(w.id)}
                    data-testid={`chat-chip-instance-${w.id}`}
                    className={`max-w-[140px] truncate rounded-full border px-3 py-1.5 text-[12px] font-semibold ${
                      inboxInstanceActive(w.id) ? "border-slate-800 bg-slate-800 text-white" : "border-slate-200 bg-white text-slate-600"
                    }`}
                  >
                    {w.display_name}
                  </button>
                ))}
              </div>
            </div>
          )}
          <div className="px-3 md:px-3 lg:px-4 pt-3 md:pt-4 lg:pt-5 pb-2.5 lg:pb-3 shrink-0 border-b border-slate-200/80 bg-white/60">
            <div className="flex items-center gap-3">
              <div
                className="h-12 w-12 rounded-full flex items-center justify-center text-white font-bold text-sm shadow-md shrink-0"
                style={{ background: `linear-gradient(145deg, ${BRAND_PRIMARY}, ${BRAND_PRIMARY_DEEP})` }}
              >
                {meInitials}
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-slate-800 truncate text-[15px]">{meLabel}</div>
                <div className="text-[11px] text-slate-400 flex items-center gap-1.5">
                  <span className="inline-block h-2 w-2 rounded-full bg-cp-primary-light" style={{ boxShadow: `0 0 0 2px ${BRAND_PRIMARY}33` }} />
                  Disponível
                </div>
              </div>
            </div>
          </div>

          <div className="px-2.5 md:px-3 pt-2.5 md:pt-3 pb-2">
            <div className="flex items-center gap-3 bg-white border border-slate-200/90 rounded-2xl px-4 py-2.5 shadow-sm focus-within:ring-2 focus-within:ring-teal-400/30 focus-within:border-teal-300/50 transition">
              <Search className="h-4 w-4 text-slate-400" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar conversa ou telefone"
                data-testid="chat-search-input"
                className="bg-transparent outline-none text-[14px] text-slate-800 placeholder-slate-400 w-full"
              />
            </div>
          </div>

          <div className="px-2.5 md:px-3 py-2 flex flex-wrap md:flex-nowrap items-center gap-2">
            <button
              type="button"
              onClick={() => setFilter("all")}
              data-testid="chat-filter-all"
              className={`px-4 py-1.5 rounded-full text-[13px] font-semibold transition border ${
                filter === "all"
                  ? "text-white border-transparent shadow-sm"
                  : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
              }`}
              style={filter === "all" ? { backgroundColor: BRAND_PRIMARY } : undefined}
            >
              Todas
            </button>
            <button
              type="button"
              onClick={() => setFilter("leads")}
              data-testid="chat-filter-leads"
              className={`px-4 py-1.5 rounded-full text-[13px] font-semibold transition border ${
                filter === "leads"
                  ? "text-white border-transparent shadow-sm"
                  : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
              }`}
              style={filter === "leads" ? { backgroundColor: BRAND_PRIMARY } : undefined}
            >
              Leads
            </button>
            <button
              type="button"
              onClick={() => setFilter("unanswered")}
              data-testid="chat-filter-unanswered"
              className={`px-4 py-1.5 rounded-full text-[13px] font-semibold transition border ${
                filter === "unanswered"
                  ? "text-white border-transparent shadow-sm"
                  : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
              }`}
              style={filter === "unanswered" ? { backgroundColor: BRAND_PRIMARY } : undefined}
            >
              Não respondido
            </button>
          </div>

          {!showWaInstanceRail && (
            <div className="flex shrink-0 flex-col gap-2 px-3 pb-2">
              <div className="inline-flex rounded-xl border border-slate-200/90 bg-white p-0.5 shadow-sm">
                <button
                  type="button"
                  data-testid="chat-inbox-unified"
                  onClick={() => setInboxLayout("unified")}
                  className={`px-3 py-1 rounded-lg text-[12px] font-semibold transition ${
                    inboxLayout === "unified" ? "bg-slate-800 text-white shadow-sm" : "text-slate-600 hover:bg-slate-50"
                  }`}
                >
                  Juntos
                </button>
                <button
                  type="button"
                  data-testid="chat-inbox-separated"
                  onClick={() => setInboxLayout("separated")}
                  className={`px-3 py-1 rounded-lg text-[12px] font-semibold transition ${
                    inboxLayout === "separated" ? "bg-slate-800 text-white shadow-sm" : "text-slate-600 hover:bg-slate-50"
                  }`}
                >
                  Separado
                </button>
              </div>
            </div>
          )}

          <div className="chat-contacts-scroll flex-1 overflow-y-auto min-h-0 pb-3 pr-1" data-testid="chat-contacts-list">
            {filteredContacts.length === 0 ? (
              <div className="p-8 text-center text-slate-500 text-[13px]">
                Nenhuma conversa ainda. <br />
                Configure a conexão do WhatsApp em Conexão.
              </div>
            ) : inboxLayout === "separated" && contactSections?.length ? (
              visibleContactSections?.length ? (
                visibleContactSections.map((sec) => (
                  <div key={sec.kid} className="mb-1">
                    <div className="sticky top-0 z-[1] px-4 py-2 flex items-center gap-2 bg-[#f8f9fb]/95 backdrop-blur-sm border-b border-slate-200/60">
                      <span
                        className="text-[10px] font-bold px-2 py-0.5 rounded-md text-white truncate max-w-[min(220px,85%)]"
                        style={instanceBadgeStyle(sec.label)}
                        title={sec.label}
                      >
                        {sec.label}
                      </span>
                      <span className="text-[11px] text-slate-400">{sec.contacts.length}</span>
                    </div>
                    {sec.contacts.map((c) => (
                      <ContactRow key={c.id} contact={c} selected={selected?.id === c.id} onClick={() => openConversation(c)} />
                    ))}
                  </div>
                ))
              ) : (
                <div className="p-8 text-center text-slate-500 text-[13px]">Nenhuma conversa neste número ainda.</div>
              )
            ) : (
              filteredContacts.map((c) => (
                <ContactRow key={c.id} contact={c} selected={selected?.id === c.id} onClick={() => openConversation(c)} />
              ))
            )}
          </div>
        </aside>

        <section
          className={`flex flex-col min-h-0 relative bg-white ${selected || immersiveLayout ? "flex" : "hidden md:flex"} md:shadow-[inset_1px_0_0_rgba(226,232,240,0.9)]`}
        >
          {!selected ? (
            <div className="flex-1 flex items-center justify-center text-center px-6 bg-gradient-to-b from-white to-slate-50">
              <div className="max-w-md">
                <div
                  className="mx-auto h-36 w-36 rounded-3xl flex items-center justify-center mb-6 border shadow-md"
                  style={{
                    background: `linear-gradient(160deg, ${BRAND_PRIMARY}22, #fff)`,
                    borderColor: `${BRAND_PRIMARY}44`,
                  }}
                >
                  <svg viewBox="0 0 24 24" fill="none" className="h-16 w-16" style={{ color: BRAND_PRIMARY, opacity: 0.85 }}>
                    <path fill="currentColor" d="M19.05 4.91A9.82 9.82 0 0 0 12.04 2c-5.46 0-9.91 4.45-9.91 9.91 0 1.75.46 3.45 1.32 4.95L2.05 22l5.25-1.38a9.9 9.9 0 0 0 4.73 1.2h.01c5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.14-2.9-7.01" />
                  </svg>
                </div>
                <h2 className="font-heading text-2xl font-bold tracking-tight text-slate-800 mb-2">ConvertPix Chat</h2>
                <p className="text-[14px] text-slate-500 leading-relaxed">
                  Responda seus leads diretamente. O motor pausa automaticamente quando você entra na conversa.
                </p>
              </div>
            </div>
          ) : (
            <>
              <header className="h-[56px] px-4 flex items-center gap-3 shrink-0 border-b border-slate-200/90 bg-white">
                <button
                  type="button"
                  onClick={() => {
                    if (isGlobalChatFull) {
                      exitGlobalChatFullscreen();
                      return;
                    }
                    if (immersiveLayout) {
                      setChatImmersive(false);
                      return;
                    }
                    closeConversation();
                  }}
                  className={`p-2 text-slate-500 hover:bg-slate-100 rounded-xl ${immersiveLayout ? "flex" : "md:hidden flex"}`}
                  data-testid="chat-back-btn"
                  title={immersiveLayout ? "Mostrar conversas e painéis" : "Voltar à lista"}
                >
                  <ArrowLeft className="h-5 w-5" />
                </button>
                <button
                  type="button"
                  onClick={() => setContextOpen(!contextOpen)}
                  className={`h-10 w-10 rounded-2xl flex items-center justify-center text-white font-semibold text-sm shrink-0 shadow-md transition hover:opacity-95 overflow-hidden ${
                    selected.is_lead ? "" : "bg-slate-300"
                  }`}
                  style={selected.is_lead ? { background: `linear-gradient(145deg, ${BRAND_PRIMARY}, ${BRAND_PRIMARY_DEEP})` } : undefined}
                >
                  {selected.profile_picture_url ? (
                    <img src={selected.profile_picture_url} alt={selected.name || "Contato"} className="h-full w-full object-cover" />
                  ) : (
                    (selected.name || "?").slice(0, 2).toUpperCase()
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => setContextOpen(!contextOpen)}
                  data-testid="chat-header-info"
                  className="flex-1 min-w-0 text-left"
                >
                  <div className="text-[15px] text-slate-800 truncate flex items-center gap-2 font-semibold">
                    {selected.name}
                    {selected.is_lead && (
                      <span
                        className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-md font-bold border"
                        style={{ color: BRAND_PRIMARY, borderColor: `${BRAND_PRIMARY}44`, background: `${BRAND_PRIMARY}12` }}
                      >
                        LEAD
                      </span>
                    )}
                  </div>
                  <div className="text-[12px] text-slate-500 truncate flex items-center gap-2">
                    {selectedOnlineNow && (
                      <span className="inline-flex items-center gap-1.5 text-emerald-600 font-medium shrink-0">
                        <span className="h-2 w-2 rounded-full bg-emerald-500" />
                        Online agora
                      </span>
                    )}
                    <span className="truncate">{selected.phone}</span>
                    {context?.active_transaction && (
                      <span className="text-amber-600 font-medium shrink-0">
                        · PIX {formatCurrency(context.active_transaction.amount)} pendente
                      </span>
                    )}
                  </div>
                </button>
                <div className="hidden lg:flex items-center gap-0.5 rounded-2xl bg-slate-100/90 p-0.5 border border-slate-200/80 shrink-0">
                  <span
                    className={`px-3 py-1 rounded-xl text-[12px] font-semibold ${
                      !msgSearchOpen ? "bg-white text-slate-800 shadow-sm" : "text-slate-500"
                    }`}
                  >
                    Mensagens
                  </span>
                  <button
                    type="button"
                    onClick={() => setContextOpen(!contextOpen)}
                    className={`px-3 py-1 rounded-xl text-[12px] font-semibold transition ${
                      contextOpen ? "bg-white text-slate-800 shadow-sm" : "text-slate-500 hover:text-slate-700"
                    }`}
                  >
                    Perfil
                  </button>
                </div>
                <div className="flex items-center gap-0.5 text-slate-500">
                  <button
                    type="button"
                    onClick={toggleMute}
                    data-testid="chat-toggle-mute-btn"
                    title={selected.mute_automation ? "Retomar automação" : "Pausar automação"}
                    className={`p-2 rounded-xl transition flex items-center gap-1.5 px-2 ${
                      selected.mute_automation ? "bg-amber-50 text-amber-700 border border-amber-200" : "hover:bg-slate-100"
                    }`}
                  >
                    {selected.mute_automation ? (
                      <PauseCircle className="h-4 w-4" aria-hidden />
                    ) : (
                      <Bot className="h-4 w-4" aria-hidden />
                    )}
                    <span className="text-[11px] font-semibold uppercase tracking-wider hidden xl:inline">
                      {selected.mute_automation ? "Pausada" : "Ativa"}
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setMsgSearchOpen(!msgSearchOpen)}
                    data-testid="chat-msg-search-btn"
                    className={`p-2 rounded-xl transition ${msgSearchOpen ? "bg-teal-50 text-teal-700" : "hover:bg-slate-100"}`}
                    title="Buscar na conversa"
                  >
                    <Search className="h-5 w-5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setContextOpen(!contextOpen)}
                    data-testid="chat-toggle-context-btn"
                    className={`p-2 rounded-xl transition ${contextOpen ? "bg-teal-50 text-teal-700" : "hover:bg-slate-100"}`}
                    title={contextOpen ? "Fechar painel de perfil" : "Ver perfil do contato"}
                  >
                    {contextOpen ? <PanelRightClose className="h-5 w-5" /> : <PanelRightOpen className="h-5 w-5" />}
                  </button>
                  <div ref={chatMenuWrapRef} className="relative">
                    <button
                      type="button"
                      onClick={() => setChatMenuOpen((v) => !v)}
                      data-testid="chat-more-menu-btn"
                      className="p-2 hover:bg-slate-100 rounded-xl transition"
                      title="Mais opções"
                    >
                      <MoreVertical className="h-5 w-5" />
                    </button>
                    {chatMenuOpen && (
                      <div
                        className="absolute right-0 top-full z-40 mt-1.5 min-w-[160px] overflow-hidden rounded-lg border border-slate-200/90 bg-white p-1 shadow-lg"
                        data-testid="chat-more-menu"
                      >
                        <button
                          type="button"
                          onClick={closeConversation}
                          data-testid="chat-close-conversation-btn"
                          className="w-full rounded-md px-3 py-2 text-left text-[12px] font-medium text-slate-700 transition hover:bg-slate-100"
                        >
                          Fechar conversa
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </header>

              {msgSearchOpen && (
                <div
                  className="border-b border-slate-200/90 px-4 py-2.5 flex items-center gap-3 panel-enter bg-slate-50/90"
                  data-testid="msg-search-bar"
                >
                  <Search className="h-4 w-4 text-slate-400" />
                  <input
                    autoFocus
                    value={msgSearch}
                    onChange={(e) => {
                      setMsgSearch(e.target.value);
                      setMatchIndex(0);
                    }}
                    placeholder="Buscar mensagens nesta conversa..."
                    data-testid="msg-search-input"
                    className="flex-1 bg-transparent outline-none text-sm text-slate-800 placeholder-slate-400"
                  />
                  <span className="text-xs text-slate-500 tabular-nums">
                    {matchedIndexes.length === 0 ? (msgSearch ? "0 resultados" : "") : `${matchIndex + 1} / ${matchedIndexes.length}`}
                  </span>
                  <button
                    type="button"
                    onClick={() => navigateMatch(-1)}
                    disabled={matchedIndexes.length === 0}
                    className="p-1.5 text-slate-500 hover:bg-white rounded-lg disabled:opacity-30"
                  >
                    <ChevronUp className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => navigateMatch(1)}
                    disabled={matchedIndexes.length === 0}
                    className="p-1.5 text-slate-500 hover:bg-white rounded-lg disabled:opacity-30"
                  >
                    <ChevronDown className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setMsgSearchOpen(false);
                      setMsgSearch("");
                    }}
                    className="p-1.5 text-slate-500 hover:bg-white rounded-lg"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              )}

              {selected.mute_automation && (
                <div
                  className="bg-amber-50 border-b border-amber-200/80 px-4 py-2 flex items-center gap-2 text-xs text-amber-900 panel-enter"
                  data-testid="mute-banner"
                >
                  <PauseCircle className="h-3.5 w-3.5 shrink-0 text-amber-700" aria-hidden />
                  <span className="flex-1">
                    Automação pausada para este lead. O motor não envia mais mensagens automáticas.
                  </span>
                  <button
                    type="button"
                    onClick={toggleMute}
                    data-testid="mute-banner-resume-btn"
                    className="text-amber-700 hover:text-amber-900 font-semibold underline shrink-0"
                  >
                    Retomar
                  </button>
                </div>
              )}

              <div className="relative flex-1 min-h-0">
                <div
                  ref={messagesContainerRef}
                  onScroll={handleMessagesScroll}
                  className="chat-contacts-scroll h-full overflow-y-auto pt-4 pb-24 md:pb-20 space-y-1 min-h-0 pr-1"
                  style={{ backgroundColor: "#f8fafc", backgroundImage: `url('${CHAT_PATTERN}')`, backgroundRepeat: "repeat" }}
                  data-testid="chat-messages-area"
                >
                  {threadLoading ? (
                    <div className="flex h-full items-center justify-center text-slate-500 text-[13px]">
                      <span className="animate-pulse">Carregando conversa...</span>
                    </div>
                  ) : messages.length === 0 ? (
                    <div className="flex items-center justify-center h-full text-slate-500 text-[13px]">Nenhuma mensagem ainda.</div>
                  ) : (
                    renderedMessages
                  )}
                  <div ref={messagesEndRef} />
                </div>
                {showScrollToTop && (
                  <button
                    type="button"
                    onClick={() =>
                      messagesContainerRef.current?.scrollTo({
                        top: messagesContainerRef.current?.scrollHeight ?? 0,
                        behavior: "smooth",
                      })
                    }
                    className="absolute bottom-3 right-3 z-10 rounded-full border border-slate-300/80 bg-white/85 p-2 text-slate-500 shadow-sm backdrop-blur transition hover:text-slate-700 hover:bg-white"
                    title="Ir para o final da conversa"
                    aria-label="Ir para o final da conversa"
                    data-testid="chat-scroll-to-top"
                  >
                    <ChevronDown className="h-4 w-4" />
                  </button>
                )}
              </div>

              <footer
                className={`px-3 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] flex flex-col gap-2 shrink-0 border-t transition-colors ${
                  recording
                    ? "border-rose-200/90 bg-gradient-to-r from-rose-50/98 via-rose-50/95 to-orange-50/90"
                    : "border-slate-200/90 bg-white"
                }`}
              >
                {selected && !recording && (selected.conversation_instance_name || selected.last_message_instance_name) && (
                  <div
                    className="flex min-w-0 items-center gap-2 text-[11px] text-slate-500"
                    data-testid="chat-reply-thread-instance"
                  >
                    <Smartphone className="h-3.5 w-3.5 shrink-0 text-slate-400" aria-hidden />
                    <span className="min-w-0 leading-snug">
                      Respostas saem por{" "}
                      <span className="font-semibold text-slate-700">
                        {selected.conversation_instance_name || selected.last_message_instance_name}
                      </span>{" "}
                      (linha desta conversa).
                    </span>
                  </div>
                )}
                {attachmentPreview && !recording && (
                  <div
                    className="flex w-full min-w-0 flex-wrap items-center gap-2 rounded-2xl border border-teal-200/90 bg-gradient-to-r from-teal-50/90 to-slate-50/80 px-3 py-2.5 shadow-sm sm:flex-nowrap sm:gap-3"
                    data-testid="chat-attachment-preview"
                  >
                    {attachmentPreview.kind === "image" && attachmentPreview.previewUrl && (
                      <img
                        src={attachmentPreview.previewUrl}
                        alt=""
                        className="h-[72px] w-[72px] shrink-0 rounded-xl border border-slate-200/90 object-cover shadow-sm"
                      />
                    )}
                    {attachmentPreview.kind === "video" && attachmentPreview.previewUrl && (
                      <video
                        src={attachmentPreview.previewUrl}
                        muted
                        playsInline
                        className="h-[72px] max-w-[120px] shrink-0 rounded-xl border border-slate-200/90 bg-black object-cover shadow-sm"
                      />
                    )}
                    {attachmentPreview.kind === "document" && (
                      <div className="flex h-[72px] w-[72px] shrink-0 items-center justify-center rounded-xl border border-slate-200/90 bg-white shadow-sm">
                        <FileText className="h-9 w-9 text-slate-400" />
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-[13px] font-semibold text-slate-800">{attachmentPreview.name}</div>
                      <div className="mt-0.5 text-[11px] text-slate-500">
                        {attachmentPreview.kind === "image"
                          ? "Foto"
                          : attachmentPreview.kind === "video"
                            ? "Vídeo"
                            : "Documento"}
                        {" · "}
                        {formatFileSize(attachmentPreview.file.size)}
                      </div>
                      <div className="mt-1 text-[10px] leading-tight text-slate-500">
                        Legenda opcional no campo abaixo - envie pelo botão verde ou Enter.
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={discardAttachmentPreview}
                      disabled={sending}
                      className="shrink-0 rounded-xl px-3 py-2 text-[13px] font-medium text-slate-600 transition hover:bg-white/80 disabled:opacity-50"
                    >
                      Cancelar
                    </button>
                    <button
                      type="button"
                      onClick={confirmSendAttachment}
                      disabled={sending}
                      data-testid="chat-attachment-send-btn"
                      className="flex shrink-0 items-center gap-1.5 rounded-xl px-4 py-2.5 text-[13px] font-semibold text-white shadow-md transition hover:opacity-95 disabled:opacity-50"
                      style={{ backgroundColor: BRAND_PRIMARY }}
                      title="Enviar anexo"
                    >
                      <Send className="h-4 w-4" />
                      Enviar
                    </button>
                  </div>
                )}
                <div className="flex w-full min-h-0 min-w-0 items-end gap-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*,video/*"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) prepareAttachmentPreview(f);
                  }}
                  className="hidden"
                  data-testid="chat-file-input"
                />
                <button
                  type="button"
                  disabled={recording}
                  className="p-2.5 text-slate-400 hover:bg-slate-100 rounded-xl transition disabled:opacity-40 disabled:pointer-events-none"
                  title="Emoji"
                >
                  <Smile className="h-5 w-5" />
                </button>
                <div className="relative shrink-0" ref={attachWrapRef}>
                  <button
                    type="button"
                    onClick={() => setAttachMenuOpen((o) => !o)}
                    disabled={sending || recording || !!attachmentPreview}
                    data-testid="chat-attach-btn"
                    aria-haspopup="menu"
                    aria-expanded={attachMenuOpen}
                    className="p-2.5 text-slate-400 hover:bg-slate-100 rounded-xl transition disabled:opacity-40 disabled:pointer-events-none"
                    title={
                      attachmentPreview
                        ? "Cancele ou envie o anexo em pré-visualização para escolher outro"
                        : "Anexar foto, vídeo ou documento"
                    }
                  >
                    <Paperclip className="h-5 w-5 -rotate-45" />
                  </button>
                  {attachMenuOpen && (
                    <div
                      role="menu"
                      className="absolute bottom-full left-0 z-30 mb-2 min-w-[200px] rounded-xl border border-slate-200 bg-white py-1 shadow-lg"
                    >
                      <button
                        type="button"
                        role="menuitem"
                        className="flex w-full items-center gap-2 px-3 py-2.5 text-left text-[13px] font-medium text-slate-700 hover:bg-slate-50"
                        onClick={() =>
                          openAttachPicker(
                            "image/*,.jpg,.jpeg,.jpe,.jfif,.pjpeg,.png,.apng,.gif,.webp,.bmp,.dib,.tif,.tiff,.heic,.heif,.avif,.svg,.ico",
                            "image",
                          )
                        }
                      >
                        <ImageIcon className="h-4 w-4 text-slate-500" />
                        Foto
                      </button>
                      <button
                        type="button"
                        role="menuitem"
                        className="flex w-full items-center gap-2 px-3 py-2.5 text-left text-[13px] font-medium text-slate-700 hover:bg-slate-50"
                        onClick={() =>
                          openAttachPicker(
                            "video/*,.mp4,.m4v,.mov,.qt,.webm,.mkv,.avi,.3gp,.mpeg,.mpg,.ogv,.wmv,.flv,.ts,.m2ts",
                            "video",
                          )
                        }
                      >
                        <Video className="h-4 w-4 text-slate-500" />
                        Vídeo
                      </button>
                      <button
                        type="button"
                        role="menuitem"
                        className="flex w-full items-center gap-2 px-3 py-2.5 text-left text-[13px] font-medium text-slate-700 hover:bg-slate-50"
                        onClick={() => openAttachPicker("", "document")}
                        title="Qualquer ficheiro (PDF, Word, etc.) até ~9 MB"
                      >
                        <FileText className="h-4 w-4 text-slate-500" />
                        Documento
                      </button>
                    </div>
                  )}
                </div>
                {recording ? (
                  <div
                    className="flex-1 flex items-center gap-3 min-h-[48px] rounded-2xl border border-rose-200/90 bg-white/95 px-3 py-2 shadow-inner"
                    data-testid="chat-recording-bar"
                  >
                    <span className="chat-rec-dot" aria-hidden />
                    <div className="min-w-0 flex-1">
                      <div className="text-[13px] font-semibold text-rose-900 tracking-tight">Gravando áudio</div>
                      <div className="text-[11px] text-rose-700/85 leading-tight mt-0.5">
                        Toque em <span className="font-semibold">Parar e enviar</span> quando terminar
                      </div>
                    </div>
                    <div className="chat-rec-wave shrink-0" aria-hidden>
                      <span /><span /><span /><span /><span />
                    </div>
                    <span
                      className="shrink-0 text-[20px] font-bold tabular-nums text-rose-800 min-w-[3.25rem] text-right"
                      data-testid="chat-recording-timer"
                    >
                      {formatVoiceDurationSeconds(recordingElapsedSec) ?? "0:00"}
                    </span>
                  </div>
                ) : (
                  <div className="relative flex-1 bg-slate-50 border border-slate-200/90 rounded-2xl px-4 py-2.5 focus-within:ring-2 focus-within:ring-teal-400/25 focus-within:border-teal-300/40 transition shadow-inner">
                    <input
                      value={text}
                      onChange={(e) => setText(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                          e.preventDefault();
                          if (attachmentPreview) void confirmSendAttachment();
                          else sendText();
                        }
                      }}
                      placeholder={attachmentPreview ? "Legenda (opcional)…" : "Escreva sua mensagem…"}
                      data-testid="chat-text-input"
                      className="w-full bg-transparent outline-none text-[15px] text-slate-800 placeholder-slate-400"
                    />
                  </div>
                )}
                {!recording && (text.trim() || attachmentPreview) ? (
                  <button
                    type="button"
                    onClick={() => {
                      if (attachmentPreview) void confirmSendAttachment();
                      else sendText();
                    }}
                    disabled={sending}
                    data-testid="chat-send-btn"
                    className="p-3 text-white rounded-full transition disabled:opacity-50 shadow-lg hover:opacity-95"
                    style={{ backgroundColor: BRAND_PRIMARY }}
                    title={attachmentPreview ? "Enviar ficheiro e legenda" : "Enviar"}
                  >
                    <Send className="h-5 w-5" />
                  </button>
                ) : recording ? (
                  <button
                    type="button"
                    onClick={stopRecording}
                    data-testid="chat-stop-rec-btn"
                    className="flex items-center gap-2 pl-3 pr-4 py-2.5 bg-red-500 hover:bg-red-600 text-white rounded-2xl transition shadow-lg shrink-0"
                    title="Parar gravação e enviar o áudio"
                    aria-label="Parar e enviar áudio"
                  >
                    <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-white/20">
                      <Square className="h-4 w-4 fill-current" />
                    </span>
                    <span className="text-[13px] font-semibold pr-0.5">Parar e enviar</span>
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={startRecording}
                    data-testid="chat-rec-btn"
                    className="p-2.5 text-slate-400 hover:bg-slate-100 rounded-xl transition"
                    title="Gravar áudio"
                  >
                    <Mic className="h-5 w-5" />
                  </button>
                )}
                </div>
              </footer>
            </>
          )}
        </section>

        {showContextPanel && (
          <aside className="hidden lg:flex flex-col min-h-0 border-l border-slate-200/90 bg-[#f8f9fb] shadow-[inset_1px_0_0_rgba(226,232,240,0.9)]">
            <ContactProfilePanel
              contact={selected}
              context={context}
              onClose={() => setContextOpen(false)}
              onMarkPaid={markPaid}
              onToggleMute={toggleMute}
              onSyncProfile={syncSelectedProfile}
              onCopyPhone={copyPhone}
            />
          </aside>
        )}

        {showContextPanel && (
          <div className="lg:hidden fixed inset-0 z-40 flex">
            <button
              type="button"
              aria-label="Fechar painel"
              onClick={() => setContextOpen(false)}
              className="flex-1 bg-black/40"
            />
            <aside className="w-[88vw] max-w-[380px] h-full bg-[#f8f9fb] border-l border-slate-200/90 shadow-xl">
              <ContactProfilePanel
                contact={selected}
                context={context}
                onClose={() => setContextOpen(false)}
                onMarkPaid={markPaid}
                onToggleMute={toggleMute}
                onSyncProfile={syncSelectedProfile}
                onCopyPhone={copyPhone}
              />
            </aside>
          </div>
        )}
        {mediaViewer && (
          <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/75 backdrop-blur-sm">
            <button
              type="button"
              onClick={closeMediaViewer}
              aria-label="Fechar visualização de mídia"
              className="absolute inset-0"
            />
            <div className="relative z-[81] mx-4 flex max-h-[90vh] w-full max-w-5xl flex-col overflow-hidden rounded-xl border border-white/15 bg-slate-950/95 shadow-2xl">
              <div className="flex items-center justify-between border-b border-white/10 px-3 py-2 text-slate-200">
                <div className="truncate text-xs font-medium">{mediaViewer.name || (mediaViewer.type === "image" ? "Imagem" : "Vídeo")}</div>
                <div className="flex items-center gap-1.5">
                  {mediaViewer.type === "image" && (
                    <>
                      <button
                        type="button"
                        onClick={() => changeMediaZoom(-0.2)}
                        className="rounded-md border border-white/15 px-2 py-1 text-xs hover:bg-white/10"
                        title="Diminuir zoom"
                        aria-label="Diminuir zoom"
                      >
                        -
                      </button>
                      <button
                        type="button"
                        onClick={() => setMediaZoom(1)}
                        className="rounded-md border border-white/15 px-2 py-1 text-xs hover:bg-white/10"
                        title="Resetar zoom"
                        aria-label="Resetar zoom"
                      >
                        {Math.round(mediaZoom * 100)}%
                      </button>
                      <button
                        type="button"
                        onClick={() => changeMediaZoom(0.2)}
                        className="rounded-md border border-white/15 px-2 py-1 text-xs hover:bg-white/10"
                        title="Aumentar zoom"
                        aria-label="Aumentar zoom"
                      >
                        +
                      </button>
                    </>
                  )}
                  <button
                    type="button"
                    onClick={closeMediaViewer}
                    className="rounded-md border border-white/15 px-2 py-1 text-xs hover:bg-white/10"
                    title="Fechar"
                    aria-label="Fechar"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>
              <div className="flex min-h-0 flex-1 items-center justify-center overflow-auto bg-black/30 p-3">
                {mediaViewer.type === "image" ? (
                  <img
                    src={mediaViewer.src}
                    alt={mediaViewer.name || "Imagem"}
                    onWheel={(e) => {
                      e.preventDefault();
                      changeMediaZoom(e.deltaY < 0 ? 0.15 : -0.15);
                    }}
                    style={{ transform: `scale(${mediaZoom})`, transformOrigin: "center center" }}
                    className="max-h-[80vh] max-w-full select-none rounded-lg object-contain transition-transform duration-150"
                  />
                ) : (
                  <video
                    src={mediaViewer.src}
                    controls
                    autoPlay
                    className="max-h-[80vh] w-full rounded-lg bg-black object-contain"
                  />
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
